import { ScrollNavigation } from '../../shared/scroll-navigation';
import { PlanFeatureFeedbackBanner } from '../../core/services/subscriptions/plan-feature-feedback';
import { managedFeatureForRoute } from '../../../../../shared-util/src/lib/plan-feature-policy';
import { ApexPagination } from '../../../../../shared-ui/src/lib/pagination/apex-pagination';
import { AiRequestScope } from '../../../../../shared-util/src/lib/ai-request-scope';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, NgZone, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NavigationStart, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, filter, firstValueFrom, map, timeout, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AiApiService, AiChatResponse, AiDraftQuestion, AiDraftResponse } from '../../core/services/ai/ai.service';
import { PropertyResponseDto, UnitResponseDto } from '../../core/services/leases/lease.service';
import { MaintenanceApiService } from '../../core/services/maintenance/maintenance.service';
import { TenantResponseDto, TenantService } from '../../core/services/tenants/tenant.service';
import { AuthService, MeResponse } from '../../core/services/auth/authservice';
import { AccountingService } from '../../core/services/accounting/accounting.service';
import { AppearanceService } from '../../core/services/appearance/appearance.service';
import { DisplayPreferencesService } from '../../core/services/preferences/display-preferences.service';
import { ProfileService, UserProfile } from '../../core/services/profile/profile.service';
import { TranslatePipe } from '../../core/services/translation/translate.pipe';
import {
  OrganizationContextItem,
  OrganizationContextService,
  OrganizationNotification,
  OwnerInvitation,
  PlatformSupportSessionResponse,
  StaffInvitation,
} from '../../core/guards/organization-context.service';
import {
  LanguageOption,
  TranslationService,
} from '../../core/services/translation/translation.service';
import { SubscriptionService, SubscriptionStatusResponse, planIncludes } from '../../core/services/subscriptions/subscription.service';
import { AiDraftReviewDrawer } from '../../shared/ai-draft-review-drawer/ai-draft-review-drawer';
import { GuideSpotlight } from '../../core/setup-guide/guide-spotlight';
import { SetupGuideService } from '../../core/setup-guide/setup-guide.service';
import { resolveCurrency } from '../../../../../shared-util/src/lib/currency';
import { workspaceCalendarAllowed, hasWorkspacePermission, hasWorkspaceRole, legacyBroadAccess, onCapabilityPolicy, ownerOnlyAllowed } from '../../core/access/workspace-access';

type NavItem = {
  labelKey: string;
  icon: string;
  route?: string;
  queryParams?: Record<string, string>;
  disabled?: boolean;
  ownerOnly?: boolean;
  requiresAccountingRead?: boolean;
  /** Plan feature the page needs. Without it the item stays, marked with a lock, and opens an upgrade card. */
  planFeature?: string;
  permissions?: string[];
  roles?: string[];
  children?: NavItem[];
  /** Runs in place of navigation (the item renders as a button). */
  action?: 'setupGuide';
};

type QuickActionItem = {
  labelKey: string;
  icon: string;
  route?: string;
  queryParams?: Record<string, string>;
  disabled?: boolean;
  ownerOnly?: boolean;
  permissions?: string[];
};

type AiCommandSuggestion = {
  labelKey: string;
  promptKey: string;
  icon: string;
};

type AiCommandMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  result?: AiAssistantResult | null;
  suggestions?: string[];
};

type AiAssistantResult = AiDraftResponse | AiChatResponse;

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'apexBackofficeSidebarCollapsed';
const DISMISSED_AI_DRAFTS_STORAGE_KEY = 'apexBackofficeDismissedAiDrafts.v2';
const WORKSPACE_BRANDING_STORAGE_KEY = 'apexBackofficeWorkspaceBranding.v1';
const PLATFORM_SUPPORT_SESSION_STORAGE_KEY = 'platformSupportSessionId';
// Matches app.owner-invitations.expiration-days on the property service.
const OWNER_INVITATION_VALID_DAYS = 7;

/** Unread count after a live notification: only a newly unread item adds one; a read update of an unread item removes one. */
export function nextUnreadCount(
  current: number,
  previous: Pick<OrganizationNotification, 'read'> | null,
  next: Pick<OrganizationNotification, 'read'>,
): number {
  const wasUnread = !!previous && !previous.read;
  const isUnread = !next.read;
  return Math.max(0, current + (isUnread ? 1 : 0) - (wasUnread ? 1 : 0));
}

@Component({
  selector: 'app-backoffice-shell',
  standalone: true,
  imports: [ScrollNavigation, PlanFeatureFeedbackBanner, ApexPagination, CommonModule, RouterLink, RouterLinkActive, RouterOutlet, TranslatePipe, AiDraftReviewDrawer, GuideSpotlight],
  templateUrl: './backoffice-shell.html',
  styleUrl: './backoffice-shell.scss',
})
export class BackofficeShell {
  @ViewChild('aiConversationScroll') private aiConversationScroll?: ElementRef<HTMLElement>;
  @ViewChild('quickActionsTrigger') private quickActionsTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('quickActionsMenu') private quickActionsMenu?: ElementRef<HTMLElement>;

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly ai = inject(AiApiService);
  private readonly auth = inject(AuthService);
  private readonly accounting = inject(AccountingService);
  private readonly maintenanceApi = inject(MaintenanceApiService);
  private readonly tenantApi = inject(TenantService);
  protected readonly appearance = inject(AppearanceService);
  private readonly displayPreferences = inject(DisplayPreferencesService);
  private readonly profile = inject(ProfileService);
  private readonly organizations = inject(OrganizationContextService);
  private readonly subscriptions = inject(SubscriptionService);
  private readonly router = inject(Router);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  protected readonly translations = inject(TranslationService);
  private readonly setupGuide = inject(SetupGuideService);

  protected readonly session$ = this.auth.session$;
  protected readonly profile$ = this.profile.profile$;
  protected readonly shellUser$ = combineLatest([this.session$, this.profile$, this.profile.profileLoaded$]).pipe(
    map(([session, profile, profileLoaded]) => ({
      session,
      profile,
      displayName: this.displayName(session, profile),
      initials: this.initials(session, profile),
      avatarUrl: this.avatarUrl(session, profile),
      identityLoaded: this.identityLoaded(session, profile, profileLoaded),
    })),
  );

  protected sidebarOpen = false;
  protected sidebarCollapsed = this.readStoredSidebarState();
  protected expandedNavGroups: Record<string, boolean> = {};
  protected languageMenuOpen = false;
  protected notificationsMenuOpen = false;
  protected quickActionsMenuOpen = false;
  protected aiCommandMenuOpen = false;
  protected aiCommandText = '';
  protected aiCommandLoading = false;
  protected aiCommandErrorKey: string | null = null;
  protected aiCommandErrorMessage: string | null = null;
  protected aiCommandResult: AiAssistantResult | null = null;
  protected aiCommandMessages: AiCommandMessage[] = [];
  protected aiRecentDrafts: AiDraftResponse[] = [];
  protected aiRecentDraftsLoading = false;
  protected aiRecentDraftsLoaded = false;
  protected aiDraftCursor: string | null = null;
  protected aiRecentDraftsErrorKey: string | null = null;
  protected aiReferenceOptionsLoading = false;
  protected aiReferenceOptionsErrorKey: string | null = null;
  protected aiPropertyOptions: PropertyResponseDto[] = [];
  protected aiUnitOptions: UnitResponseDto[] = [];
  protected aiTenantOptions: TenantResponseDto[] = [];
  protected aiDraftUploads: Record<string, string[]> = {};
  private dismissedAiDraftIds = this.readDismissedAiDraftIds();
  protected aiDraftDrawerOpen = false;
  protected activeAiDraftId: string | null = null;
  protected activeAiDraftOrgId: string | null = null;
  protected workspaceMenuOpen = false;
  protected notificationsLoading = false;
  protected unreadNotificationCount = 0;
  protected notifications: OrganizationNotification[] = [];
  protected workspaceOptions: OrganizationContextItem[] = [];
  // Workspace resolution is asynchronous; signal reads notify the shell and its plan banner.
  private readonly activeWorkspaceIdState = signal<string | null>(null);
  protected get activeWorkspaceId(): string | null { return this.activeWorkspaceIdState(); }
  protected set activeWorkspaceId(value: string | null) { this.activeWorkspaceIdState.set(value); }
  protected canCreateOrganization = false;
  protected workspaceLoading = true;
  protected workspaceLogoUrl: string | null = null;
  protected workspaceName = '';
  protected subscriptionViewOnlyNotice = '';
  protected platformSupportSession: PlatformSupportSessionResponse | null = null;
  protected platformSupportSessionLoading = false;
  protected platformSupportSessionEnding = false;
  protected platformSupportSessionError: string | null = null;
  private activeWorkspaceSubscriptionStatus: SubscriptionStatusResponse | null = null;
  private activeWorkspaceSubscriptionLoaded = false;
  /** The active workspace's subscription, for marking menu items its plan doesn't include. */
  protected readonly activeWorkspacePlan = signal<SubscriptionStatusResponse | null>(null);
  private readonly accountingNavigationAccess = signal<{ orgId: string; canRead: boolean | null } | null>(null);
  private accountingNavigationRequest = 0;
  private notificationOrgId: string | null = null;
  private notificationRefreshPromise: Promise<void> | null = null;
  private notificationEventSource: EventSource | null = null;
  private notificationRefreshIntervalId: number | null = null;
  private workspaceContextPromise: Promise<string | null> | null = null;
  private hostWorkspaceOrgIdPromise: Promise<string | null> | null = null;
  private aiConversationScrollFrame: number | null = null;
  private shellDestroyed = false;
  private readonly readOnlySupportClickListener = (event: Event) => this.handleReadOnlySupportClick(event);
  private readonly readOnlySupportSubmitListener = (event: Event) => this.handleReadOnlySupportSubmit(event);
  private readonly readOnlySupportFormChangeListener = (event: Event) => this.handleReadOnlySupportFormChange(event);
  protected readonly languageOptions = this.translations.languageOptions;

  protected get primaryNav(): NavItem[] {
    const propertyManagementWorkspace = this.isPropertyManagementWorkspace();
    if (propertyManagementWorkspace) {
      return this.filterNavItems([
        {
          labelKey: 'nav.dashboard',
          icon: 'dashboard',
          route: '/dashboard',
          queryParams: this.withCurrentOrg({}),
        },
        {
          labelKey: 'nav.propertyManagement',
          icon: 'business',
          route: '/properties',
          queryParams: this.withCurrentOrg({}),
          permissions: ['PROPERTY_VIEW'],
        },
        {
          labelKey: 'nav.landlordsOwners',
          icon: 'real_estate_agent',
          route: '/owners',
          queryParams: this.withCurrentOrg({}),
          permissions: ['OWNER_VIEW'],
        },
        {
          labelKey: 'nav.tenants',
          icon: 'groups',
          children: [
            {
              labelKey: 'nav.allTenants',
              icon: 'groups',
              route: '/tenants',
              queryParams: this.withCurrentOrg({}),
              permissions: ['TENANT_VIEW'],
            },
            {
              labelKey: 'nav.applications',
              icon: 'assignment_ind',
              route: '/applications',
              queryParams: this.withCurrentOrg({}),
              permissions: ['APPLICATION_VIEW'],
            },
            {
              labelKey: 'nav.contacts',
              icon: 'contacts',
              route: '/contacts',
              queryParams: this.withCurrentOrg({}),
              permissions: ['TENANT_VIEW'],
            },
          ],
        },
        {
          labelKey: 'nav.leases',
          icon: 'description',
          children: [
            {
              labelKey: 'nav.leaseDirectory',
              icon: 'description',
              route: '/leases',
              queryParams: this.withCurrentOrg({}),
              permissions: ['LEASE_VIEW'],
            },
            {
              labelKey: 'nav.leaseTemplates',
              icon: 'contract',
              route: '/leases/templates',
              queryParams: this.withCurrentOrg({}),
              permissions: ['LEASE_VIEW'],
            },
          ],
        },
        {
          labelKey: 'nav.staff',
          icon: 'badge',
          route: '/staff',
          queryParams: this.withCurrentOrg({}),
          permissions: ['MEMBERSHIP_VIEW'],
        },
        {
          labelKey: 'nav.paymentsPayouts',
          icon: 'payments',
          route: '/transactions',
          queryParams: this.withCurrentOrg({}),
          children: [
            {
              labelKey: 'nav.openBalances',
              icon: 'account_balance_wallet',
              route: '/balances',
              queryParams: this.withCurrentOrg({}),
              permissions: ['PAYMENT_VIEW', 'LEASE_VIEW'],
            },
            {
              labelKey: 'nav.transactions',
              icon: 'sync_alt',
              route: '/transactions',
              queryParams: this.withCurrentOrg({}),
              permissions: ['PAYMENT_VIEW'],
            },
            {
              labelKey: 'nav.accounting',
              icon: 'account_balance',
              route: '/accounting',
              queryParams: this.withCurrentOrg({}),
              requiresAccountingRead: true,
              planFeature: 'ACCOUNTING',
              permissions: ['ACCOUNTING_READ'],
            },
            {
              labelKey: 'nav.paymentSetup',
              icon: 'payments',
              route: '/payments/stripe-connect',
              queryParams: this.withCurrentOrg({}),
              ownerOnly: true,
              permissions: ['PAYMENT_SETTINGS_MANAGE'],
            },
          ],
        },
        {
          labelKey: 'nav.documents',
          icon: 'documents_custom',
          route: '/documents',
          queryParams: this.withCurrentOrg({}),
          permissions: ['DOCUMENT_VIEW'],
        },
        {
          labelKey: 'nav.inspections',
          icon: 'fact_check',
          route: '/inspections',
          queryParams: this.withCurrentOrg({}),
          permissions: ['INSPECTION_VIEW'],
        },
        {
          labelKey: 'nav.maintenance',
          icon: 'build',
          children: [
            {
              labelKey: 'nav.maintenanceWorkOrders',
              icon: 'build',
              route: '/maintenance',
              queryParams: this.withCurrentOrg({}),
              permissions: ['MAINTENANCE_VIEW'],
            },
            {
              labelKey: 'nav.maintenanceCompanies',
              icon: 'engineering',
              route: '/maintenance/companies',
              queryParams: this.withCurrentOrg({}),
              permissions: ['MAINTENANCE_VIEW'],
            },
          ],
        },
        {
          labelKey: 'nav.calendar',
          icon: 'calendar_month',
          route: '/calendar',
          queryParams: this.withCurrentOrg({}),
          permissions: ['CALENDAR_VIEW'],
        },
        {
          labelKey: 'nav.reports',
          icon: 'monitoring',
          route: '/reports',
          queryParams: this.withCurrentOrg({}),
          permissions: ['REPORT_VIEW'],
        },
        {
          labelKey: 'nav.communications',
          icon: 'forum',
          route: '/communications',
          queryParams: this.withCurrentOrg({}),
          permissions: ['COMMUNICATION_VIEW'],
        },
        {
          labelKey: 'nav.operations',
          icon: 'fact_check',
          route: '/operations',
          queryParams: this.withCurrentOrg({}),
          permissions: ['REPORT_VIEW', 'PAYMENT_VIEW', 'OWNER_VIEW', 'ORG_VIEW'],
        },
      ]);
    }

    return this.filterNavItems([
      {
        labelKey: 'nav.dashboard',
        icon: 'dashboard',
        route: '/dashboard',
        queryParams: this.withCurrentOrg({}),
      },
      {
        labelKey: 'nav.properties',
        icon: 'business',
        route: '/properties',
        queryParams: this.withCurrentOrg({}),
        permissions: ['PROPERTY_VIEW'],
      },
      {
        labelKey: 'nav.tenants',
        icon: 'groups',
        children: [
          {
            labelKey: 'nav.allTenants',
            icon: 'groups',
            route: '/tenants',
            queryParams: this.withCurrentOrg({}),
            permissions: ['TENANT_VIEW'],
          },
          {
            labelKey: 'nav.applications',
            icon: 'assignment_ind',
            route: '/applications',
            queryParams: this.withCurrentOrg({}),
            permissions: ['APPLICATION_VIEW'],
          },
          {
            labelKey: 'nav.contacts',
            icon: 'contacts',
            route: '/contacts',
            queryParams: this.withCurrentOrg({}),
            permissions: ['TENANT_VIEW'],
          },
        ],
      },
      {
        labelKey: 'nav.leases',
        icon: 'description',
        children: [
          {
            labelKey: 'nav.leaseDirectory',
            icon: 'description',
            route: '/leases',
            queryParams: this.withCurrentOrg({}),
            permissions: ['LEASE_VIEW'],
          },
          {
            labelKey: 'nav.leaseTemplates',
            icon: 'contract',
            route: '/leases/templates',
            queryParams: this.withCurrentOrg({}),
            permissions: ['LEASE_VIEW'],
          },
        ],
      },
      {
        labelKey: 'nav.paymentsPayouts',
        icon: 'payments',
        route: '/transactions',
        queryParams: this.withCurrentOrg({}),
        children: [
          {
            labelKey: 'nav.openBalances',
            icon: 'account_balance_wallet',
            route: '/balances',
            queryParams: this.withCurrentOrg({}),
            permissions: ['PAYMENT_VIEW', 'LEASE_VIEW'],
          },
          {
            labelKey: 'nav.transactions',
            icon: 'sync_alt',
            route: '/transactions',
            queryParams: this.withCurrentOrg({}),
            permissions: ['PAYMENT_VIEW'],
          },
          {
            labelKey: 'nav.accounting',
            icon: 'account_balance',
            route: '/accounting',
            queryParams: this.withCurrentOrg({}),
            requiresAccountingRead: true,
            planFeature: 'ACCOUNTING',
            permissions: ['ACCOUNTING_READ'],
          },
          {
            labelKey: 'nav.paymentSetup',
            icon: 'payments',
            route: '/payments/stripe-connect',
            queryParams: this.withCurrentOrg({}),
            ownerOnly: true,
              permissions: ['PAYMENT_SETTINGS_MANAGE'],
          },
        ],
      },
      {
        labelKey: 'nav.documents',
        icon: 'documents_custom',
        route: '/documents',
        queryParams: this.withCurrentOrg({}),
        permissions: ['DOCUMENT_VIEW'],
      },
      {
        labelKey: 'nav.inspections',
        icon: 'fact_check',
        route: '/inspections',
        queryParams: this.withCurrentOrg({}),
        permissions: ['INSPECTION_VIEW'],
      },
      {
        labelKey: 'nav.maintenance',
        icon: 'build',
        children: [
          {
            labelKey: 'nav.maintenanceWorkOrders',
            icon: 'build',
            route: '/maintenance',
            queryParams: this.withCurrentOrg({}),
            permissions: ['MAINTENANCE_VIEW'],
          },
          {
            labelKey: 'nav.maintenanceCompanies',
            icon: 'engineering',
            route: '/maintenance/companies',
            queryParams: this.withCurrentOrg({}),
            permissions: ['MAINTENANCE_VIEW'],
          },
        ],
      },
      {
        labelKey: 'nav.calendar',
        icon: 'calendar_month',
        route: '/calendar',
        queryParams: this.withCurrentOrg({}),
        permissions: ['CALENDAR_VIEW'],
      },
      {
        labelKey: 'nav.reports',
        icon: 'monitoring',
        route: '/reports',
        queryParams: this.withCurrentOrg({}),
        permissions: ['REPORT_VIEW'],
      },
      {
        labelKey: 'nav.communications',
        icon: 'forum',
        route: '/communications',
        queryParams: this.withCurrentOrg({}),
        permissions: ['COMMUNICATION_VIEW'],
      },
      {
        labelKey: 'nav.operations',
        icon: 'fact_check',
        route: '/operations',
        queryParams: this.withCurrentOrg({}),
        permissions: ['REPORT_VIEW', 'PAYMENT_VIEW', 'OWNER_VIEW', 'ORG_VIEW'],
      },
    ]);
  }

  protected get supportNav(): NavItem[] {
    return [
      // Owners/admins only (same rule as the dashboard checklist): reopens a hidden setup guide.
      ...(this.setupGuide.canUse() ? [{ labelKey: 'nav.setupGuide', icon: 'checklist', action: 'setupGuide' as const }] : []),
      {
        labelKey: 'nav.support',
        icon: 'headset_mic',
        route: '/support',
        queryParams: this.withCurrentOrg({}),
      },
      {
        labelKey: 'nav.settings',
        icon: 'settings',
        route: '/settings',
        queryParams: this.withCurrentOrg({ section: 'profile' }),
      },
    ];
  }

  protected get quickActions(): QuickActionItem[] {
    const actions: QuickActionItem[] = [];

    if (this.canAddPropertyFromSubscription()) {
      actions.push({
        labelKey: 'header.quickAction.addProperty',
        icon: 'pi-building',
        route: '/properties/add',
        queryParams: this.withCurrentOrg({ mode: 'select' }),
        permissions: ['PROPERTY_CREATE'],
      });
    }

    actions.push(
      {
        labelKey: 'header.quickAction.recordPayment',
        icon: 'pi-credit-card',
        route: '/transactions',
        queryParams: this.withCurrentOrg({ action: 'record-payment' }),
        permissions: ['PAYMENT_RECORD'],
      },
    );

    return this.filterQuickActions(actions);
  }

  protected readonly aiCommandSuggestions: AiCommandSuggestion[] = [
    {
      labelKey: 'header.ai.suggestion.maintenance.label',
      promptKey: 'header.ai.suggestion.maintenance.prompt',
      icon: 'build',
    },
    {
      labelKey: 'header.ai.suggestion.report.label',
      promptKey: 'header.ai.suggestion.report.prompt',
      icon: 'monitoring',
    },
    {
      labelKey: 'header.ai.suggestion.lease.label',
      promptKey: 'header.ai.suggestion.lease.prompt',
      icon: 'description',
    },
    {
      labelKey: 'header.ai.suggestion.property.label',
      promptKey: 'header.ai.suggestion.property.prompt',
      icon: 'apartment',
    },
  ];

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.closeTransientShellUi());

    const brandingRefreshSubscription = this.organizations.brandingRefresh$.subscribe((orgId) => {
      if (!this.notificationOrgId || this.notificationOrgId === orgId) {
        void this.initializeWorkspaceBranding(orgId);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.shellDestroyed = true;
      this.aiRequests.invalidate();
      if (typeof document !== 'undefined') {
        document.removeEventListener('click', this.readOnlySupportClickListener, true);
        document.removeEventListener('submit', this.readOnlySupportSubmitListener, true);
        document.removeEventListener('input', this.readOnlySupportFormChangeListener, true);
        document.removeEventListener('change', this.readOnlySupportFormChangeListener, true);
      }
      if (this.aiConversationScrollFrame !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(this.aiConversationScrollFrame);
        this.aiConversationScrollFrame = null;
      }
      brandingRefreshSubscription.unsubscribe();
      this.stopNotificationRefreshPolling();
      this.closeNotificationStream();
    });
    if (typeof window !== 'undefined') {
      document.addEventListener('click', this.readOnlySupportClickListener, true);
      document.addEventListener('submit', this.readOnlySupportSubmitListener, true);
      document.addEventListener('input', this.readOnlySupportFormChangeListener, true);
      document.addEventListener('change', this.readOnlySupportFormChangeListener, true);
      void this.resolveNotificationOrgId();
      void this.loadPlatformSupportSession();
      void this.initializeWorkspaceBranding();
      void this.initializeNotificationStream();
      this.startNotificationRefreshPolling();
    }
  }

  protected closeSidebar(): void {
    this.sidebarOpen = false;
  }

  protected runSupportAction(item: NavItem): void {
    if (item.action !== 'setupGuide') return;
    this.closeSidebar();
    const orgId = this.activeWorkspaceId || this.currentOrgId();
    if (orgId) this.setupGuide.reopen(orgId);
    void this.router.navigate(['/dashboard'], { queryParams: this.withCurrentOrg({}) }).then(() => {
      if (typeof document === 'undefined') return;
      // The card renders once the dashboard has loaded; bring it into view without moving focus unexpectedly.
      setTimeout(() => document.querySelector('[data-setup-guide]')?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 400);
    });
  }

  protected toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  protected toggleNavigation(): void {
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      this.storeSidebarState();
      return;
    }

    this.toggleSidebar();
  }

  protected get activeLanguage(): LanguageOption {
    return this.translations.activeLanguage;
  }

  protected toggleLanguageMenu(): void {
    this.languageMenuOpen = !this.languageMenuOpen;
    this.quickActionsMenuOpen = false;
    this.closeAiConversation();
    this.workspaceMenuOpen = false;
  }

  protected toggleQuickActionsMenu(): void {
    this.quickActionsMenuOpen = !this.quickActionsMenuOpen;
    this.closeAiConversation();
    this.languageMenuOpen = false;
    this.notificationsMenuOpen = false;
    this.workspaceMenuOpen = false;
    this.syncNotificationView();
    if (this.quickActionsMenuOpen) this.quickActionButtons()[0]?.focus();
  }

  protected openQuickActionsFromKeyboard(event: Event, last = false): void {
    event.preventDefault();
    if (!this.quickActionsMenuOpen) this.toggleQuickActionsMenu();
    const buttons = this.quickActionButtons();
    buttons[last ? buttons.length - 1 : 0]?.focus();
  }

  protected handleQuickActionsKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      this.quickActionsMenuOpen = false;
      this.quickActionsTrigger?.nativeElement.focus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = this.quickActionButtons();
    if (!buttons.length) return;
    const index = buttons.indexOf(event.target as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
      : (index + (event.key === 'ArrowUp' ? -1 : 1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }

  private quickActionButtons(): HTMLButtonElement[] {
    return Array.from(this.quickActionsMenu?.nativeElement.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
  }

  protected get canUseAi(): boolean {
    const workspace = this.activeWorkspace();
    return Boolean(workspace && (!onCapabilityPolicy(workspace) || hasWorkspacePermission(workspace, 'AI_USE')));
  }

  protected openAiCommandMenu(): void {
    if (!this.canUseAi) return;
    this.aiCommandMenuOpen = true;
    this.quickActionsMenuOpen = false;
    this.languageMenuOpen = false;
    this.notificationsMenuOpen = false;
    this.workspaceMenuOpen = false;
    this.syncNotificationView();
    this.scheduleAiConversationScroll('auto');
    void this.loadAiRecentDrafts();
  }

  protected toggleAiCommandMenu(): void {
    if (this.aiCommandMenuOpen) {
      this.closeAiConversation();
      this.syncNotificationView();
      return;
    }

    this.openAiCommandMenu();
  }

  protected useAiSuggestion(suggestion: AiCommandSuggestion): void {
    void this.submitAiPrompt(this.translations.translate(suggestion.promptKey));
  }

  protected useAiFollowUp(prompt: string): void {
    void this.submitAiPrompt(prompt);
  }

  private async submitAiPrompt(prompt: string): Promise<void> {
    this.aiCommandText = prompt;
    this.aiCommandErrorKey = null;
    this.aiCommandErrorMessage = null;
    this.aiCommandResult = null;
    await this.submitAiCommand();
  }

  protected updateAiCommandText(event: Event): void {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    this.aiCommandText = input?.value ?? '';
  }

  private conversationDraft: AiDraftResponse | null = null;
  private readonly aiRequests = new AiRequestScope();
  private aiWorkspaceGeneration = 0;
  private aiConversationOrg: string | null = null;

  protected cancelAiResponse(): void {
    if (this.aiCommandLoading) this.aiRecentDraftsLoaded = false;
    this.aiRequests.invalidate();
    this.aiCommandLoading = false;
  }

  protected closeAiConversation(): void {
    this.cancelAiResponse();
    this.aiCommandMenuOpen = false;
  }

  private resetAiWorkspace(): void {
    this.aiWorkspaceGeneration++;
    this.clearAiConversation();
    this.aiConversationOrg = null;
    this.aiCommandText = '';
    this.aiDraftCursor = null; this.aiRecentDrafts = []; this.aiRecentDraftsLoaded = false; this.aiRecentDraftsLoading = false;
    this.aiPropertyOptions = []; this.aiUnitOptions = []; this.aiTenantOptions = [];
    this.aiReferenceOptionsLoading = false;
    this.aiDraftDrawerOpen = false; this.activeAiDraftId = null; this.activeAiDraftOrgId = null;
  }


  protected async submitAiCommand(): Promise<void> {
    if (!this.canUseAi) return;
    const command = this.aiCommandText.trim();
    if (!command || this.aiCommandLoading) {
      return;
    }

    const ticket = this.aiRequests.begin(this.activeWorkspaceId || '');
    const generation = this.aiWorkspaceGeneration;
    this.aiCommandLoading = true;
    const orgId = await this.resolveNotificationOrgId().catch(() => null);
    if (!this.aiRequests.owns(ticket) || generation !== this.aiWorkspaceGeneration || this.shellDestroyed) return;
    if (orgId && this.activeWorkspaceId !== orgId) { this.cancelAiResponse(); return; }
    if (!orgId) {
      this.aiCommandLoading = false;
      this.aiCommandErrorKey = 'header.ai.error.noWorkspace';
      this.aiCommandErrorMessage = null;
      this.aiCommandResult = null;
      return;
    }

    this.aiCommandLoading = true;
    this.aiCommandErrorKey = null;
    this.aiCommandErrorMessage = null;
    this.aiCommandResult = null;
    if (this.aiConversationOrg !== orgId) {
      this.aiCommandMessages = []; this.conversationDraft = null;
    }
    this.aiConversationOrg = orgId;
    const current = () => this.aiRequests.owns(ticket) && generation === this.aiWorkspaceGeneration
      && this.activeWorkspaceId === orgId && !this.shellDestroyed;
    const conversation = this.aiCommandMessages.slice(-16).map((message) => ({
      role: message.role,
      text: message.text,
    }));
    const userMessage: AiCommandMessage = {
      id: this.createUiId(),
      role: 'user',
      text: command,
    };
    this.aiCommandMessages = [
      ...this.aiCommandMessages,
      userMessage,
    ].slice(-30);
    this.aiCommandText = '';
    this.aiCommandMenuOpen = true;
    this.syncNotificationView();
    this.scheduleAiConversationScroll('smooth');

    try {
      const routeContext = this.currentAiRouteContext();
      const aiPayload = {
        prompt: command,
        activeDraftId: this.conversationDraft?.organizationId === orgId ? this.conversationDraft.id : null,
        expectedUpdatedAt: this.conversationDraft?.organizationId === orgId ? this.conversationDraft.updatedAt : null,
        context: {
          conversation,
          route: routeContext,
        },
        source: {
          sourceModule: 'backoffice',
          sourceRefId: routeContext['propertyId'] ?? null,
          locale: this.activeLanguage.code,
          timezone: this.displayPreferences.currentTimeZone,
        },
      };
      const turn = await firstValueFrom(this.ai.assistantTurn(orgId, aiPayload).pipe(takeUntil(ticket.cancelled)));
      if (!current()) return;
      const result: AiAssistantResult | null = turn.draft ?? turn.message;
      if (!result) throw new Error('The assistant returned an empty response. Please try again.');
      if (turn.draft) this.rememberConversationDraft(turn.draft);
      this.aiCommandResult = result;
      const assistantMessage: AiCommandMessage = {
        id: this.createUiId(),
        role: 'assistant',
        text: this.aiAssistantText(result),
        result,
        suggestions: this.aiAssistantSuggestions(result),
      };
      this.aiCommandMessages = [
        ...this.aiCommandMessages,
        assistantMessage,
      ].slice(-30);
      if (this.isAiDraftResult(result)) {
        this.aiRecentDrafts = this.mergeAiDraft(result, this.aiRecentDrafts);
        this.aiRecentDraftsLoaded = true;
        void this.loadAiReferenceOptionsForDraft(result);
      }
      this.aiCommandMenuOpen = true;
    } catch (error) {
      if (!current()) return;
      const refusal = this.aiGuidanceRefusal(error);
      if (refusal) {
        // A deliberate "no reviewed guidance" answer is a reply, not a failure: no error banner.
        const refusalMessage: AiCommandMessage = { id: this.createUiId(), role: 'assistant', text: refusal };
        this.aiCommandMessages = [...this.aiCommandMessages, refusalMessage].slice(-30);
        return;
      }
      console.warn('[BackofficeShell] Apex AI command failed:', error);
      this.aiCommandErrorKey = 'header.ai.error.failed';
      this.aiCommandErrorMessage = this.extractAiCommandErrorMessage(error);
      const errorMessage: AiCommandMessage = {
        id: this.createUiId(),
        role: 'assistant',
        text: this.aiCommandErrorMessage || this.translations.translate('header.ai.error.failed'),
      };
      this.aiCommandMessages = [
        ...this.aiCommandMessages,
        errorMessage,
      ].slice(-30);
    } finally {
      if (!current()) return;
      this.aiCommandLoading = false;
      this.syncNotificationView();
      this.scheduleAiConversationScroll('smooth');
    }
  }

  private rememberConversationDraft(draft: AiDraftResponse): void {
    this.conversationDraft = draft.intent === 'CREATE_PROPERTY_WITH_UNITS'
      && ['PENDING_REVIEW', 'NEEDS_MORE_INFO'].includes(draft.status) ? draft : null;
  }

  protected handleAiDraftLoaded(draft: AiDraftResponse): void {
    if (this.shellDestroyed || draft.organizationId !== this.activeWorkspaceId) return;
    this.rememberConversationDraft(draft);
  }

  protected get shouldClearAiFooter(): boolean {
    const currentPath = (this.router.url || '').split('?')[0];
    return [
      '/balances',
      '/leases',
      '/maintenance',
      '/settings',
      '/transactions',
    ].some((route) => currentPath === route || currentPath.startsWith(`${route}/`));
  }

  protected navItemIsActive(item: NavItem): boolean {
    const currentPath = (this.router.url || '').split('?')[0];
    const routes = [
      item.route,
      ...(item.children ?? []).map((child) => child.route),
    ].filter((route): route is string => Boolean(route));

    return routes.some((route) => currentPath === route || (route !== '/dashboard' && currentPath.startsWith(`${route}/`)));
  }

  protected navGroupIsExpanded(item: NavItem): boolean {
    return this.expandedNavGroups[item.labelKey] ?? this.navItemIsActive(item);
  }

  protected toggleNavGroup(item: NavItem): void {
    if (this.sidebarCollapsed) {
      const target = this.navGroupNavigationTarget(item);
      if (target?.route) {
        this.sidebarOpen = false;
        void this.router.navigate([target.route], {
          queryParams: target.queryParams,
        });
        return;
      }
    }

    this.expandedNavGroups = {
      ...this.expandedNavGroups,
      [item.labelKey]: !this.navGroupIsExpanded(item),
    };
  }

  private navGroupNavigationTarget(item: NavItem): Pick<NavItem, 'route' | 'queryParams'> | null {
    if (item.route) {
      const matchingChild = item.children?.find((child) => child.route === item.route && !child.disabled);
      if (!item.children?.length || matchingChild) {
        return {
          route: item.route,
          queryParams: item.queryParams ?? matchingChild?.queryParams,
        };
      }
    }

    return item.children?.find((child) => child.route && !child.disabled) ?? null;
  }

  private currentAiRouteContext(): Record<string, string | null> {
    const url = this.router.url || '';
    const [path, queryString = ''] = url.split('?');
    const query = new URLSearchParams(queryString);
    const propertyMatch = path.match(/\/properties\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i);
    return {
      path,
      propertyId: propertyMatch?.[1] ? decodeURIComponent(propertyMatch[1]) : query.get('propertyId'),
      unitId: query.get('unitId'),
      tenantId: query.get('tenantId'),
      leaseId: query.get('leaseId'),
      requestId: query.get('requestId'),
    };
  }

  private aiAssistantText(result: AiAssistantResult): string {
    if ('answer' in result && result.answer?.trim()) {
      return result.answer.trim();
    }

    return result.summary || (this.isAiDraftResult(result)
          ? this.translations.translate('header.ai.draftFallback')
          : this.translations.translate('header.ai.answerFallback'));
  }

  private aiAssistantSuggestions(result: AiAssistantResult): string[] {
    if ('suggestions' in result && Array.isArray(result.suggestions)) {
      return result.suggestions
        .map((suggestion) => suggestion?.trim())
        .filter((suggestion): suggestion is string => Boolean(suggestion))
        .slice(0, 4);
    }

    if (this.hasAiMissingDetails(result)) {
      const prompts = [
        this.hasAiMissingAlias(result, 'property') ? this.translations.translate('header.ai.followUp.chooseProperty', 'Choose a property') : null,
        this.hasAiMissingAlias(result, 'unit') ? this.translations.translate('header.ai.followUp.chooseUnit', 'Choose a unit') : null,
        this.hasAiMissingAlias(result, 'tenant') ? this.translations.translate('header.ai.followUp.chooseTenant', 'Choose a tenant') : null,
      ];
      return prompts.filter((prompt): prompt is string => Boolean(prompt));
    }

    if (this.isReviewableAiDraftResult(result)) {
      return [
        this.translations.translate('header.ai.followUp.explainDraft', 'What will this draft do?'),
        this.translations.translate('header.ai.followUp.risks', 'What should I verify before approval?'),
      ];
    }

    return [];
  }

  /** The AI service's safe refusal (HTTP 422, code AI_GUIDANCE_UNAVAILABLE): its message, or null for any other error. */
  /** Deliberate answers sent as 422: no reviewed guidance, or a reply held back for review. */
  private aiGuidanceRefusal(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse) || error.status !== 422) return null;
    const body = error.error as { code?: unknown; message?: unknown } | null;
    if (body?.code !== 'AI_GUIDANCE_UNAVAILABLE' && body?.code !== 'AI_RESPONSE_REVIEW_REQUIRED') return null;
    return typeof body.message === 'string' && body.message.trim() ? body.message.trim() : null;
  }

  private extractAiCommandErrorMessage(error: unknown): string | null {
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string') {
        return error.error.trim() || error.message || null;
      }

      const message = error.error?.message || error.error?.error || error.message;
      return typeof message === 'string' && message.trim() ? message.trim() : null;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return null;
  }

  protected openAiDraftDrawer(draftId: string, orgId = this.activeWorkspaceId || this.currentOrgId()): void {
    if (!this.canUseAi) return;
    if (!draftId || !orgId) {
      return;
    }

    this.activeAiDraftId = draftId;
    this.activeAiDraftOrgId = orgId;
    this.aiDraftDrawerOpen = true;
    this.closeAiConversation();
  }

  protected openAiDraftFromResult(result: AiAssistantResult | null): void {
    if (!this.isReviewableAiDraftResult(result)) {
      return;
    }

    this.openAiDraftDrawer(result.id, result.organizationId);
  }

  protected isAiDraftResult(result: AiAssistantResult | null): result is AiDraftResponse {
    if (!result || !result.id || !result.organizationId || !result.status) {
      return false;
    }

    const intent = (result.intent ?? '').toUpperCase();
    const isReadOnlyIntent = intent === 'EXPLAIN_REPORT_SUMMARY' || intent === 'SEARCH_RECORDS' || intent === 'UNKNOWN';
    const actions = result.actions ?? [];
    const hasWriteAction = actions.some((action) => this.isWriteAiAction(action));
    const hasMissingFields = (result.missingFields ?? []).filter((field) => field !== 'review_ai_output').length > 0;

    return Boolean(result.requiresApproval || hasWriteAction || hasMissingFields || !isReadOnlyIntent);
  }

  protected isReviewableAiDraftResult(result: AiAssistantResult | null): result is AiDraftResponse {
    return this.isAiDraftResult(result)
      && (result?.status === 'PENDING_REVIEW' || result?.status === 'NEEDS_MORE_INFO');
  }


  protected hasAiMissingDetails(result: AiAssistantResult | null): result is AiDraftResponse {
    return this.isReviewableAiDraftResult(result) && this.aiVisibleMissingFields(result).length > 0;
  }

  protected hasAiMissingAlias(result: AiAssistantResult | null, group: 'property' | 'unit' | 'tenant'): boolean {
    if (result?.intent === 'CREATE_PROPERTY_WITH_UNITS') return false;
    if (!this.isAiDraftResult(result)) {
      return false;
    }
    const aliases = this.aiReferenceAliases(group);
    return this.aiVisibleMissingFields(result).some((field) => aliases.has(this.normalizeAiFieldName(field)));
  }

  protected aiMissingDetailsPrompt(result: AiAssistantResult | null): string {
    if (this.hasAiMissingAlias(result, 'property')) {
      return this.translations.translate('header.ai.missing.propertyQuestion', 'Which property should I use?');
    }
    if (this.hasAiMissingAlias(result, 'unit')) {
      return this.translations.translate('header.ai.missing.unitQuestion', 'Which unit should I attach it to?');
    }
    if (this.hasAiMissingAlias(result, 'tenant')) {
      return this.translations.translate('header.ai.missing.tenantQuestion', 'Which tenant should I attach it to?');
    }
    return this.translations.translate('header.ai.missing.question', 'I need one more detail before I can continue.');
  }

  protected aiDraftReferenceValue(result: AiAssistantResult | null, field: 'propertyId' | 'unitId' | 'tenantId'): string {
    if (!this.isAiDraftResult(result)) {
      return '';
    }
    const draftValue = this.stringAiValue(result.draft?.[field]);
    if (draftValue) {
      return draftValue;
    }
    const actionPayload = (result.actions ?? [])
      .map((action) => action.payload)
      .find((payload): payload is Record<string, unknown> => !!payload && typeof payload === 'object');
    return this.stringAiValue(actionPayload?.[field]);
  }

  protected aiDraftQuestions(result: AiAssistantResult | null): AiDraftQuestion[] {
    if (!this.isAiDraftResult(result)) {
      return [];
    }
    const rawQuestions = result.draft?.['_questions'];
    if (!Array.isArray(rawQuestions)) {
      return [];
    }

    return rawQuestions
      .filter((question): question is AiDraftQuestion => !!question && typeof question === 'object')
      .filter((question) => !!question.field?.trim() && !!(question.question || question.label))
      .slice(0, 14);
  }

  protected shouldShowAiDraftQuestion(result: AiAssistantResult | null, question: AiDraftQuestion): boolean {
    const field = question.field?.trim();
    if (!field) {
      return false;
    }
    const value = this.aiDraftQuestionValue(result, question);
    if (value) {
      return false;
    }
    const referenceGroup = this.aiQuestionReferenceGroup(question);
    if (!referenceGroup) {
      return true;
    }
    return !this.hasAiMissingAlias(result, referenceGroup);
  }

  protected aiDraftQuestionValue(result: AiAssistantResult | null, question: AiDraftQuestion): string {
    if (!this.isAiDraftResult(result)) {
      return '';
    }
    const field = question.field?.trim();
    if (!field) {
      return '';
    }
    const draftValue = this.stringAiValue(this.nestedAiValue(result.draft ?? {}, field));
    if (draftValue) {
      return draftValue;
    }
    const actionPayload = (result.actions ?? [])
      .map((action) => action.payload)
      .find((payload): payload is Record<string, unknown> => !!payload && typeof payload === 'object');
    return this.stringAiValue(this.nestedAiValue(actionPayload ?? {}, field));
  }

  protected aiQuestionType(question: AiDraftQuestion): string {
    const type = question.type?.trim().toLowerCase();
    if (this.isAiUploadQuestion(question)) {
      return 'upload';
    }
    return ['textarea', 'number', 'date', 'select', 'reference'].includes(type ?? '') ? type! : 'text';
  }

  protected aiUploadAccept(question: AiDraftQuestion): string {
    const signature = `${question.field ?? ''} ${question.label ?? ''} ${question.question ?? ''}`.toLowerCase();
    if (/(document|pdf|lease|agreement|file|attachment)/.test(signature) && !/(photo|image|picture)/.test(signature)) {
      return '.pdf,.doc,.docx,image/*';
    }
    return 'image/*';
  }

  protected aiDraftUploadNames(result: AiAssistantResult | null, question: AiDraftQuestion): string[] {
    const uploadKey = this.aiDraftQuestionKey(result, question);
    const selected = this.aiDraftUploads[uploadKey];
    if (selected?.length) {
      return selected;
    }
    if (!this.isAiDraftResult(result)) {
      return [];
    }
    return this.aiValueList(this.nestedAiValue(result.draft ?? {}, question.field?.trim() ?? ''));
  }

  protected aiQuestionReferenceGroup(question: AiDraftQuestion): 'property' | 'unit' | 'tenant' | null {
    const field = this.normalizeAiFieldName(question.field ?? '');
    if (this.aiReferenceAliases('property').has(field)) {
      return 'property';
    }
    if (this.aiReferenceAliases('unit').has(field)) {
      return 'unit';
    }
    if (this.aiReferenceAliases('tenant').has(field)) {
      return 'tenant';
    }
    return null;
  }

  protected async resolveAiDraftQuestion(
    result: AiAssistantResult | null,
    question: AiDraftQuestion,
    rawValue: string,
  ): Promise<void> {
    if (!this.isReviewableAiDraftResult(result)) {
      return;
    }
    const field = question.field?.trim();
    const value = rawValue.trim();
    if (!field || !value) {
      return;
    }

    if (field === 'propertyId') {
      await this.loadAiUnits(result.organizationId, value);
    }

    const updated = await this.patchAiDraftField(result, field, value).catch((error) => {
      console.warn('[BackofficeShell] failed to update AI question:', error);
      this.aiReferenceOptionsErrorKey = 'header.ai.error.referencesFailed';
      return null;
    });

    if (!updated) {
      this.syncNotificationView();
      return;
    }

    this.applyUpdatedAiDraft(updated);
    await this.loadAiReferenceOptionsForDraft(updated);
    this.syncNotificationView();
  }

  protected async resolveAiDraftUploadQuestion(
    result: AiAssistantResult | null,
    question: AiDraftQuestion,
    event: Event,
  ): Promise<void> {
    if (!this.isReviewableAiDraftResult(result)) {
      return;
    }
    const field = question.field?.trim();
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files ?? []);
    if (!field || !files.length) {
      return;
    }

    const fileSummaries = files.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream',
      lastModified: file.lastModified,
    }));
    this.aiDraftUploads[this.aiDraftQuestionKey(result, question)] = fileSummaries.map((file) => file.name);

    const updated = await this.patchAiDraftField(result, field, fileSummaries).catch((error) => {
      console.warn('[BackofficeShell] failed to update AI upload question:', error);
      this.aiReferenceOptionsErrorKey = 'header.ai.error.referencesFailed';
      return null;
    });

    if (input) {
      input.value = '';
    }
    if (!updated) {
      this.syncNotificationView();
      return;
    }

    this.applyUpdatedAiDraft(updated);
    this.syncNotificationView();
  }

  protected aiPropertyLabel(property: PropertyResponseDto): string {
    return property.name?.trim() || property.address?.trim() || this.translations.translate('header.ai.unnamedProperty', 'Unnamed property');
  }

  protected aiPropertySubLabel(property: PropertyResponseDto): string {
    return property.address?.trim() || property.id;
  }

  protected aiUnitLabel(unit: UnitResponseDto): string {
    return unit.unitNumber?.trim() || `Unit ${unit.id.slice(0, 8)}`;
  }

  protected aiUnitSubLabel(unit: UnitResponseDto): string {
    return unit.rentAmount != null ? `${this.displayPreferences.formatMoney(unit.rentAmount)}/mo` : unit.id;
  }

  protected aiTenantLabel(tenant: TenantResponseDto): string {
    const fullName = [tenant.firstName, tenant.lastName].filter(Boolean).join(' ').trim();
    return fullName || tenant.email || `Tenant ${tenant.id.slice(0, 8)}`;
  }

  protected aiTenantSubLabel(tenant: TenantResponseDto): string {
    return tenant.email || tenant.id;
  }

  protected async resolveAiMissingReference(
    result: AiAssistantResult | null,
    field: 'propertyId' | 'unitId' | 'tenantId',
    rawValue: string,
  ): Promise<void> {
    if (!this.isReviewableAiDraftResult(result) || !rawValue) {
      return;
    }

    const value = rawValue.trim();
    if (field === 'propertyId') {
      await this.loadAiUnits(result.organizationId, value);
    }

    const updated = await this.patchAiDraftReference(result, field, value).catch((error) => {
      console.warn('[BackofficeShell] failed to update AI missing detail:', error);
      this.aiReferenceOptionsErrorKey = 'header.ai.error.referencesFailed';
      return null;
    });

    if (!updated) {
      this.syncNotificationView();
      return;
    }

    this.applyUpdatedAiDraft(updated);
    await this.loadAiReferenceOptionsForDraft(updated);
    this.syncNotificationView();
  }


  private async loadAiReferenceOptionsForDraft(draft: AiDraftResponse): Promise<void> {
    if (draft.intent === 'CREATE_PROPERTY_WITH_UNITS' || draft.organizationId !== this.activeWorkspaceId) return;
    const generation = this.aiWorkspaceGeneration;
    const current = () => generation === this.aiWorkspaceGeneration && draft.organizationId === this.activeWorkspaceId && !this.shellDestroyed;
    const questionReferenceGroups = new Set(
      this.aiDraftQuestions(draft)
        .map((question) => this.aiQuestionReferenceGroup(question))
        .filter((group): group is 'property' | 'unit' | 'tenant' => !!group),
    );
    const needsPropertyOptions = this.hasAiMissingAlias(draft, 'property') || questionReferenceGroups.has('property');
    const needsUnitOptions = this.hasAiMissingAlias(draft, 'unit') || questionReferenceGroups.has('unit');
    const needsTenantOptions = this.hasAiMissingAlias(draft, 'tenant') || questionReferenceGroups.has('tenant');

    if (!this.hasAiMissingDetails(draft) && questionReferenceGroups.size === 0) {
      return;
    }

    this.aiReferenceOptionsLoading = true;
    this.aiReferenceOptionsErrorKey = null;
    this.syncNotificationView();
    try {
      const tasks: Promise<void>[] = [];
      if (needsPropertyOptions && this.aiPropertyOptions.length === 0) {
        tasks.push(firstValueFrom(this.maintenanceApi.listProperties(draft.organizationId)).then((page) => {
          if (!current()) return;
          this.aiPropertyOptions = page.content ?? [];
        }));
      }
      if (needsTenantOptions && this.aiTenantOptions.length === 0) {
        tasks.push(firstValueFrom(this.tenantApi.listTenants(draft.organizationId)).then((page) => {
          if (!current()) return;
          this.aiTenantOptions = page.content ?? [];
        }));
      }
      const propertyId = this.aiDraftReferenceValue(draft, 'propertyId');
      if ((needsUnitOptions || propertyId) && propertyId) {
        tasks.push(this.loadAiUnits(draft.organizationId, propertyId));
      }
      await Promise.all(tasks);
    } catch (error) {
      if (!current()) return;
      console.warn('[BackofficeShell] failed to load AI reference options:', error);
      this.aiReferenceOptionsErrorKey = 'header.ai.error.referencesFailed';
    } finally {
      if (!current()) return;
      this.aiReferenceOptionsLoading = false;
      this.syncNotificationView();
      this.scheduleAiConversationScroll('smooth');
    }
  }

  private async loadAiUnits(orgId: string, propertyId: string): Promise<void> {
    if (!orgId || !propertyId) {
      this.aiUnitOptions = [];
      return;
    }
    const generation = this.aiWorkspaceGeneration;
    const page = await firstValueFrom(this.maintenanceApi.listUnits(orgId, propertyId));
    if (generation !== this.aiWorkspaceGeneration || orgId !== this.activeWorkspaceId || this.shellDestroyed) return;
    this.aiUnitOptions = page.content ?? [];
  }

  private async patchAiDraftReference(
    draft: AiDraftResponse,
    field: 'propertyId' | 'unitId' | 'tenantId',
    value: string,
  ): Promise<AiDraftResponse> {
    const nextDraft = { ...(draft.draft ?? {}), [field]: value };
    const nextActions = (draft.actions ?? []).map((action) => ({
      ...action,
      payload: action.payload && typeof action.payload === 'object'
        ? { ...action.payload, [field]: value }
        : action.payload,
    }));
    const missingFields = this.removeAiMissingFieldGroup(
      draft.missingFields ?? [],
      field === 'propertyId' ? 'property' : field === 'unitId' ? 'unit' : 'tenant',
    );

    return firstValueFrom(this.ai.updateDraft(draft.organizationId, draft.id, {
      expectedUpdatedAt: draft.updatedAt,
      summary: draft.summary ?? null,
      missingFields,
      draft: nextDraft,
      actions: nextActions,
    }));
  }

  private async patchAiDraftField(
    draft: AiDraftResponse,
    field: string,
    value: unknown,
  ): Promise<AiDraftResponse> {
    const nextDraft = this.setNestedAiValue({ ...(draft.draft ?? {}) }, field, value);
    const nextActions = (draft.actions ?? []).map((action) => ({
      ...action,
      payload: action.payload && typeof action.payload === 'object'
        ? this.setNestedAiValue({ ...action.payload }, field, value)
        : action.payload,
    }));
    const missingFields = (draft.missingFields ?? [])
      .filter((candidate) => this.normalizeAiFieldName(candidate) !== this.normalizeAiFieldName(field));

    return firstValueFrom(this.ai.updateDraft(draft.organizationId, draft.id, {
      expectedUpdatedAt: draft.updatedAt,
      summary: draft.summary ?? null,
      missingFields,
      draft: nextDraft,
      actions: nextActions,
    }));
  }

  private applyUpdatedAiDraft(updated: AiDraftResponse): void {
    if (this.shellDestroyed || updated.organizationId !== this.activeWorkspaceId) return;
    this.rememberConversationDraft(updated);
    this.aiCommandResult = updated;
    this.aiCommandMessages = this.aiCommandMessages.map((message) => message.result?.id === updated.id
      ? { ...message, result: updated, text: this.aiAssistantText(updated), suggestions: this.aiAssistantSuggestions(updated) }
      : message,
    );
    this.aiRecentDrafts = this.mergeAiDraft(updated, this.aiRecentDrafts);
    this.scheduleAiConversationScroll('smooth');
  }

  private aiVisibleMissingFields(result: AiDraftResponse): string[] {
    return (result.missingFields ?? []).filter((field) => field !== 'review_ai_output');
  }

  private removeAiMissingFieldGroup(fields: string[], group: 'property' | 'unit' | 'tenant'): string[] {
    const aliases = this.aiReferenceAliases(group);
    return fields.filter((field) => !aliases.has(this.normalizeAiFieldName(field)));
  }

  private aiReferenceAliases(group: 'property' | 'unit' | 'tenant'): Set<string> {
    if (group === 'property') {
      return new Set(['property', 'propertyid', 'propertyname']);
    }
    if (group === 'unit') {
      return new Set(['unit', 'unitid', 'unitnumber', 'unitname']);
    }
    return new Set(['tenant', 'tenantid', 'tenantname', 'resident', 'residentid']);
  }

  private normalizeAiFieldName(field: string): string {
    return field.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private isAiUploadQuestion(question: AiDraftQuestion): boolean {
    const type = question.type?.trim().toLowerCase();
    if (['upload', 'uploads', 'file', 'files', 'image', 'images', 'photo', 'photos', 'attachment', 'attachments', 'media'].includes(type ?? '')) {
      return true;
    }
    const signature = this.normalizeAiFieldName(`${question.field ?? ''} ${question.label ?? ''} ${question.question ?? ''}`);
    return /(photo|photos|image|images|picture|pictures|screenshot|screenshots|attachment|attachments|upload|uploads|media|document|documents|file|files)/.test(signature);
  }

  private aiDraftQuestionKey(result: AiAssistantResult | null, question: AiDraftQuestion): string {
    return `${this.isAiDraftResult(result) ? result.id : 'draft'}:${question.field?.trim() ?? 'field'}`;
  }

  private stringAiValue(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    if (typeof value === 'boolean') {
      return String(value);
    }
    const values = this.aiValueList(value);
    if (values.length) {
      return values.join(', ');
    }
    return '';
  }

  private aiValueList(value: unknown): string[] {
    if (!Array.isArray(value)) {
      if (value && typeof value === 'object') {
        const name = this.stringAiValue((value as Record<string, unknown>)['name']);
        return name ? [name] : [];
      }
      return [];
    }
    return value
      .map((item) => {
        if (typeof item === 'string') {
          return item.trim();
        }
        if (item && typeof item === 'object') {
          return this.stringAiValue((item as Record<string, unknown>)['name']);
        }
        return '';
      })
      .filter((item) => !!item);
  }

  private nestedAiValue(payload: Record<string, unknown>, field: string): unknown {
    if (!field.includes('.')) {
      return payload[field];
    }
    let current: unknown = payload;
    for (const part of field.split('.')) {
      if (!current || typeof current !== 'object') {
        return null;
      }
      current = (current as Record<string, unknown>)[part];
    }
    return current;
  }

  private setNestedAiValue(payload: Record<string, unknown>, field: string, value: unknown): Record<string, unknown> {
    const parts = field.split('.').filter(Boolean);
    if (parts.length === 0) {
      return payload;
    }
    if (parts.length === 1) {
      payload[parts[0]] = value;
      return payload;
    }
    const [head, ...tail] = parts;
    const existing = payload[head];
    const child = existing && typeof existing === 'object' && !Array.isArray(existing)
      ? { ...(existing as Record<string, unknown>) }
      : {};
    payload[head] = this.setNestedAiValue(child, tail.join('.'), value);
    return payload;
  }

  protected aiCommandResultTitle(result: AiAssistantResult | null): string {
    return this.isAiDraftResult(result)
      ? this.translations.translate('header.ai.draftReady')
      : this.translations.translate('header.ai.answerReady');
  }

  protected clearAiConversation(): void {
    this.cancelAiResponse();
    this.conversationDraft = null;
    this.aiCommandMessages = [];
    this.aiCommandResult = null;
    this.aiCommandErrorKey = null;
    this.aiCommandErrorMessage = null;
    this.scheduleAiConversationScroll('auto');
  }

  protected aiMessageTitle(message: AiCommandMessage): string {
    if (message.role === 'user') {
      return this.translations.translate('header.ai.you');
    }

    if (message.result?.provider === 'POLICY') {
      return this.translations.translate('header.ai.evidenceGuidance', 'Apex guidance');
    }

    return this.isAiDraftResult(message.result ?? null)
      ? this.translations.translate('header.ai.draftReady')
      : this.translations.translate('header.ai.answerReady');
  }

  private isWriteAiAction(action: { method?: string | null; requiresApproval?: boolean | null }): boolean {
    if (action.requiresApproval) {
      return true;
    }

    const method = (action.method ?? '').toUpperCase();
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  }

  protected closeAiDraftDrawer(): void {
    this.aiDraftDrawerOpen = false;
  }

  protected handleAiDraftChanged(draft: AiDraftResponse): void {
    if (this.shellDestroyed || draft.organizationId !== this.activeWorkspaceId) return;
    this.rememberConversationDraft(draft);
    const status = (draft.status || '').toUpperCase();
    const isClosedDraft = status === 'REJECTED'
      || status === 'APPROVED'
      || status === 'EXECUTED'
      || status === 'EXPIRED'
      || status === 'FAILED'
      || status === 'EXECUTION_PENDING';

    this.aiCommandResult = isClosedDraft && this.aiCommandResult?.id === draft.id ? null : draft;
    this.aiCommandMessages = this.aiCommandMessages.map((message) => {
      if (message.result?.id !== draft.id) {
        return message;
      }

      return {
        ...message,
        result: draft,
        text: draft.summary || message.text,
      };
    });

    if (isClosedDraft) {
      const statusMessage: AiCommandMessage = {
        id: this.createUiId(),
        role: 'assistant',
        text: this.aiDraftDecisionMessage(status, draft),
      };
      this.aiCommandMessages = [
        ...this.aiCommandMessages,
        statusMessage,
      ].slice(-30);
      if (status === 'REJECTED' || status === 'EXPIRED') {
        this.aiRecentDrafts = this.aiRecentDrafts.filter((candidate) => candidate.id !== draft.id);
        this.dismissedAiDraftIds = new Set([...this.dismissedAiDraftIds, ...this.aiDraftDismissKeys(draft)]);
        this.storeDismissedAiDraftIds();
        this.scheduleAiConversationScroll('smooth');
        return;
      }

      this.dismissedAiDraftIds.delete(draft.id);
      this.storeDismissedAiDraftIds();
      this.aiRecentDrafts = this.mergeAiDraft(draft, this.aiRecentDrafts);
      this.scheduleAiConversationScroll('smooth');
      return;
    }

    this.aiRecentDrafts = this.mergeAiDraft(draft, this.aiRecentDrafts);
    this.scheduleAiConversationScroll('smooth');
  }

  private aiDraftDecisionMessage(status: string, draft?: AiDraftResponse): string {
    if (draft?.executionProtocol && draft.execution?.state === 'UNKNOWN') {
      return 'The work order may already exist. Open the saved draft to check its status or retry the same request.';
    }
    if (draft?.executionProtocol && draft.execution?.state === 'IN_FLIGHT') {
      return 'The work order request is awaiting confirmation. Open the saved draft to check its status.';
    }
    if (draft?.executionProtocol && draft.execution?.state === 'REJECTED') {
      return 'Maintenance rejected this attempt. Open the saved draft to review it before retrying the same request.';
    }
    if (status === 'EXECUTION_PENDING') {
      return this.translations.translate('header.ai.draftExecutionPending');
    }
    if (status === 'EXECUTED') {
      return this.translations.translate('header.ai.draftExecuted');
    }
    if (status === 'REJECTED') {
      return this.translations.translate('header.ai.draftRejected');
    }
    if (status === 'FAILED') {
      return this.translations.translate('header.ai.draftFailed');
    }

    return this.translations.translate('header.ai.draftApproved');
  }

  protected aiDraftStatusLabelKey(status: string | null | undefined): string {
    return `aiDraft.status.${(status || 'PENDING_REVIEW').toString().toLowerCase()}`;
  }

  protected aiDraftStatusFallback(status: string | null | undefined): string {
    return (status || 'PENDING_REVIEW')
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  protected aiDraftIntentLabelKey(intent: string | null | undefined): string {
    return `aiDraft.intent.${this.aiDraftIntentTranslationKey(intent)}`;
  }

  protected aiDraftIntentFallback(intent: string | null | undefined): string {
    return (intent || 'UNKNOWN')
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  protected async dismissAiRecentDraft(event: Event, draftId: string): Promise<void> {
    event.stopPropagation();
    if (!draftId) {
      return;
    }

    const draft = this.aiRecentDrafts.find((candidate) => candidate.id === draftId) ?? null;
    if (draft && this.needsAiExecutionReview(draft)) return;
    this.dismissedAiDraftIds = new Set([
      ...this.dismissedAiDraftIds,
      ...(draft ? this.aiDraftDismissKeys(draft) : [draftId]),
    ]);
    this.storeDismissedAiDraftIds();
    this.aiRecentDrafts = this.aiRecentDrafts.filter((draft) => draft.id !== draftId);

    if (!draft || !this.isReviewableAiDraftResult(draft)) {
      return;
    }

    const rejected = await firstValueFrom(this.ai.rejectDraft(draft.organizationId, draft.id, {
      expectedUpdatedAt: draft.updatedAt,
      notes: this.translations.translate('header.ai.dismissedFromRecentDrafts', 'Dismissed from recent drafts.'),
      requestExecution: false,
    })).catch((error) => {
      console.warn('[BackofficeShell] failed to reject dismissed AI draft:', error);
      return null;
    });

    if (rejected) {
      this.applyUpdatedAiDraft(rejected);
    }
  }

  protected toggleWorkspaceMenu(): void {
    if (this.sidebarCollapsed || (this.workspaceOptions.length <= 1 && !this.canCreateOrganization)) {
      return;
    }

    this.workspaceMenuOpen = !this.workspaceMenuOpen;
    this.languageMenuOpen = false;
    this.notificationsMenuOpen = false;
    this.quickActionsMenuOpen = false;
  }

  protected async switchWorkspace(organization: OrganizationContextItem): Promise<void> {
    if (organization.orgId === this.activeWorkspaceId) {
      this.workspaceMenuOpen = false;
      return;
    }

    this.resetAiWorkspace();
    this.workspaceLoading = true;
    this.rememberWorkspace(organization.orgId);
    this.organizations.clearOrganizationContextCache();
    this.activeWorkspaceId = organization.orgId;
    void this.loadAccountingNavigationAccess(organization.orgId);
    this.notificationOrgId = organization.orgId;
    this.workspaceName = organization.orgName?.trim() || '';
    this.workspaceLogoUrl = null;
    this.notifications = [];
    this.unreadNotificationCount = 0;
    this.workspaceMenuOpen = false;
    this.closeSidebar();
    this.closeNotificationStream();
    await this.initializeWorkspaceBranding(organization.orgId);
    this.openNotificationStream(organization.orgId);
    void this.refreshNotifications();
    window.location.href = this.workspaceDashboardUrl(organization);
  }

  protected createOrganization(): void {
    if (!this.canCreateOrganization) {
      this.workspaceMenuOpen = false;
      window.location.href = this.publicSetupUrl('/subscription/select');
      return;
    }

    this.workspaceMenuOpen = false;
    this.closeSidebar();
    window.location.href = this.publicSetupUrl('/organization/create');
  }

  protected get activeWorkspaceRole(): string {
    if (this.workspaceLoading) {
      return '';
    }
    const organization = this.workspaceOptions.find((item) => item.orgId === this.activeWorkspaceId);
    if (this.workspaceIsOwnerPortal(organization)) {
      return this.translations.translate('workspace.role.ownerPortal', 'Owner portal');
    }
    return this.workspaceRoleLabel(organization?.orgRole);
  }

  protected isPropertyManagementWorkspace(): boolean {
    if (this.workspaceLoading) {
      return false;
    }

    const activeOrganization = this.workspaceOptions.find((item) => item.orgId === this.activeWorkspaceId);
    return this.workspaceIsPropertyManager(activeOrganization);
  }

  protected isOwnerPortalWorkspace(): boolean {
    if (this.workspaceLoading) {
      return false;
    }

    return this.workspaceIsOwnerPortal(this.activeWorkspace());
  }

  private filterNavItems(items: NavItem[]): NavItem[] {
    return items
      .map((item) => ({
        ...item,
        children: item.children?.filter((child) => this.canAccessWorkspaceItem(child)),
      }))
      .filter((item) => {
        const childCount = item.children?.length ?? 0;
        return childCount > 0 || this.canAccessWorkspaceItem(item);
      });
  }

  private filterQuickActions(items: QuickActionItem[]): QuickActionItem[] {
    return items.filter((item) => this.canAccessWorkspaceItem(item));
  }

  private canAccessWorkspaceItem(item: Pick<NavItem, 'ownerOnly' | 'permissions' | 'roles' | 'requiresAccountingRead'>): boolean {
    const organization = this.activeWorkspace();
    if (!organization) {
      return false;
    }

    if (item.permissions?.includes('CALENDAR_VIEW') && !workspaceCalendarAllowed(organization)) return false;

    if (item.requiresAccountingRead) {
      const access = this.accountingNavigationAccess();
      const canRead = access?.orgId === organization.orgId ? access.canRead : null;
      // Organization context owns navigation; the accounting page authorizes data and actions.
      const organizationOwner = !onCapabilityPolicy(organization) && organization.owner === true && organization.orgRole === 'OWNER';
      if (!organizationOwner && canRead !== true) return false;
    }

    if (item.ownerOnly && !ownerOnlyAllowed(organization, item.permissions ?? [])) {
      return false;
    }

    if (item.permissions?.length && !this.hasAnyWorkspacePermission(organization, item.permissions)) {
      return false;
    }

    if (item.roles?.length && !this.hasAnyWorkspaceRole(organization, item.roles)) {
      return false;
    }

    return true;
  }

  private activeWorkspace(): OrganizationContextItem | null {
    if (this.workspaceLoading) {
      return null;
    }

    return this.workspaceOptions.find((item) => item.orgId === this.activeWorkspaceId) ?? null;
  }

  private hasAnyWorkspacePermission(organization: OrganizationContextItem, permissions: string[]): boolean {
    return hasWorkspacePermission(organization, ...permissions);
  }

  private hasAnyWorkspaceRole(organization: OrganizationContextItem, roles: string[]): boolean {
    return hasWorkspaceRole(organization, roles);
  }

  private workspaceIsPropertyManager(organization: OrganizationContextItem | null | undefined): boolean {
    const orgType = (organization?.orgType || '').toUpperCase();
    const portalMode = (organization?.portalMode || '').toUpperCase();

    return portalMode === 'PROPERTY_MANAGER'
      || orgType === 'PROPERTY_MANAGER'
      || orgType === 'PROPERTY_MANAGEMENT';
  }

  private workspaceIsOwnerPortal(organization: OrganizationContextItem | null | undefined): boolean {
    const role = (organization?.orgRole || '').toUpperCase();
    const portalMode = (organization?.portalMode || '').toUpperCase();
    return role === 'LANDLORD_PORTAL' || portalMode === 'OWNER_PORTAL';
  }

  protected workspaceRoleLabel(role: string | null | undefined): string {
    const normalized = (role || 'ORGANIZATION').toUpperCase().replace(/[\s-]+/g, '_');
    const roleKey = this.workspaceRoleKey(normalized);
    if (roleKey) {
      return this.translations.translate(roleKey);
    }

    return normalized
      .toLowerCase()
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private workspaceRoleKey(role: string): string | null {
    const roleKeys: Record<string, string> = {
      ACCOUNTANT: 'workspace.role.accountant',
      ADMIN: 'workspace.role.admin',
      ORG_ADMIN: 'workspace.role.admin',
      LANDLORD: 'workspace.role.landlord',
      MAINTENANCE_MANAGER: 'workspace.role.maintenanceManager',
      MAINTENANCE_STAFF: 'workspace.role.maintenanceStaff',
      MANAGER: 'workspace.role.manager',
      ORGANIZATION: 'workspace.role.organization',
      OWNER: 'workspace.role.owner',
      LANDLORD_PORTAL: 'workspace.role.ownerPortal',
      PROPERTY_MANAGER: 'workspace.role.propertyManager',
      ROLE_ADMIN: 'workspace.role.admin',
      ROLE_SUPER_ADMIN: 'workspace.role.superAdmin',
      STAFF: 'workspace.role.staff',
      SUPER_ADMIN: 'workspace.role.superAdmin',
      TENANT: 'workspace.role.tenant',
    };

    return roleKeys[role] ?? null;
  }

  protected workspaceIsInactive(organization: OrganizationContextItem): boolean {
    return organization.subscriptionActive === false || String(organization.status ?? 'ACTIVE').toUpperCase() !== 'ACTIVE';
  }

  protected workspaceStateLabel(organization: OrganizationContextItem): string {
    const status = String(organization.status ?? 'ACTIVE').toUpperCase();
    if (status === 'SUSPENDED') {
      return 'Workspace suspended';
    }
    if (status === 'ARCHIVED') {
      return 'Workspace archived';
    }
    if (organization.subscriptionActive === false) {
      return 'Subscription inactive';
    }
    return this.workspaceRoleLabel(organization.orgRole);
  }

  protected workspaceInitials(name: string | null | undefined): string {
    return (name || 'Workspace')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'W';
  }

  protected handleWorkspaceLogoError(): void {
    this.workspaceLogoUrl = null;
  }

  protected async runQuickAction(action: QuickActionItem): Promise<void> {
    if (action.disabled || !action.route) {
      return;
    }

    this.quickActionsMenuOpen = false;
    await this.router.navigate([action.route], {
      queryParams: action.queryParams,
    });
  }

  protected openProfile(): void {
    this.sidebarOpen = false;
    void this.router.navigate(['/settings'], {
      queryParams: this.withCurrentOrg({ section: 'profile' }),
    });
  }

  protected async refreshAiRecentDrafts(): Promise<void> {
    this.dismissedAiDraftIds = new Set([...this.dismissedAiDraftIds, ...this.readDismissedAiDraftIds()]);
    this.aiRecentDraftsLoaded = false;
    this.aiDraftCursor = null;
    this.aiRecentDraftsErrorKey = null;
    this.syncNotificationView();
    await this.loadAiRecentDrafts();
  }

  protected async toggleNotificationsMenu(): Promise<void> {
    this.notificationsMenuOpen = !this.notificationsMenuOpen;
    this.quickActionsMenuOpen = false;
    this.closeAiConversation();
    this.languageMenuOpen = false;
    this.workspaceMenuOpen = false;
    this.syncNotificationView();
    if (this.notificationsMenuOpen) {
      await this.refreshNotifications();
    }
  }

  protected async markNotificationRead(notification: OrganizationNotification): Promise<void> {
    const pendingInvitation = this.isPendingInvitationNotification(notification);
    if (!notification.read) {
      if (!pendingInvitation) {
        if (!this.notificationOrgId) return;
        await firstValueFrom(this.organizations.markNotificationRead(this.notificationOrgId, notification.id));
      }
      notification.read = true;
      this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - 1);
      this.syncNotificationView();
    }
    this.notificationsMenuOpen = false;
    this.syncNotificationView();
    const targetUrl = this.notificationTargetUrl(notification);
    if (targetUrl) {
      if (/^https?:\/\//i.test(targetUrl)) {
        window.location.href = targetUrl;
        return;
      }
      await this.router.navigateByUrl(targetUrl);
    }
  }

  protected async markAllNotificationsRead(): Promise<void> {
    if (!this.notificationOrgId) return;
    await firstValueFrom(this.organizations.markAllNotificationsRead(this.notificationOrgId));
    this.notifications = this.notifications.map((notification) => ({ ...notification, read: true }));
    this.unreadNotificationCount = 0;
    this.syncNotificationView();
  }

  protected closeNotificationsMenu(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.notificationsMenuOpen = false;
    this.syncNotificationView();
  }

  protected notificationTimeLabel(notification: OrganizationNotification): string {
    const timestamp = Date.parse(notification.createdAt);
    if (Number.isNaN(timestamp)) return '';
    const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
    if (minutes < 1) return this.translations.translate('common.time.justNow');
    if (minutes < 60) {
      return this.translations
        .translate('common.time.minutesAgo')
        .replace('{count}', String(minutes));
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return this.translations
        .translate('common.time.hoursAgo')
        .replace('{count}', String(hours));
    }
    return this.translations
      .translate('common.time.daysAgo')
      .replace('{count}', String(Math.floor(hours / 24)));
  }

  protected notificationTitle(notification: OrganizationNotification): string {
    const type = this.notificationTranslationType(notification);
    if (!type) return notification.title;

    const translated = this.translations.translate(`notification.${type}.title`, '');
    return translated || notification.title;
  }

  protected notificationMessage(notification: OrganizationNotification): string {
    const type = this.notificationTranslationType(notification);
    if (type === 'LATE_FEE_APPLIED' || type === 'OVERDUE_RENT') {
      const count = this.notificationCount(notification);
      const key = count === 1
        ? `notification.${type}.message.one`
        : `notification.${type}.message.many`;
      const translated = this.translations.translate(key, '');
      if (translated) {
        return translated.replace('{count}', String(count));
      }
    }

    return notification.message;
  }

  protected selectLanguage(language: LanguageOption): void {
    this.translations.setLanguage(language.code);
    this.languageMenuOpen = false;
    this.notificationsMenuOpen = false;
    this.quickActionsMenuOpen = false;
    this.closeAiConversation();
    this.workspaceMenuOpen = false;
  }

  private normalizedNotificationType(notification: OrganizationNotification): string {
    return (notification.type || '').trim().toUpperCase();
  }

  private notificationTranslationType(notification: OrganizationNotification): string {
    const type = this.normalizedNotificationType(notification);
    if (type === 'LATE_FEE_APPLIED' || type === 'OVERDUE_RENT') {
      return type;
    }

    const title = (notification.title || '').trim().toLowerCase();
    if (title === 'late fee applied') return 'LATE_FEE_APPLIED';
    if (title === 'overdue rent alert') return 'OVERDUE_RENT';

    return type;
  }

  private notificationCount(notification: OrganizationNotification): number {
    const match = notification.message?.match(/\b(\d+)\b/);
    const count = match?.[1] ? Number(match[1]) : 1;
    return Number.isFinite(count) && count > 0 ? count : 1;
  }

  private notificationTargetUrl(notification: OrganizationNotification): string {
    const routedUrl = this.notificationRoutedUrl(notification);
    if (routedUrl) {
      return routedUrl;
    }

    const actionUrl = (notification.actionUrl || '').trim();
    if (actionUrl && !/^javascript:/i.test(actionUrl)) {
      return /^https?:\/\//i.test(actionUrl) || actionUrl.startsWith('/') ? actionUrl : `/${actionUrl}`;
    }

    const value = [
      notification.type,
      notification.title,
      notification.referenceType,
    ].map((part) => part ?? '').join(' ').toUpperCase();
    if (value.includes('OWNER')) return '/owners';
    if (value.includes('STAFF')) return '/staff';
    if (value.includes('RENTAL_APPLICATION') || value.includes('APPLICATION')) return '/applications';
    if (value.includes('PAYMENT_SETUP')) return '/payments/stripe-connect';
    if (value.includes('PAYMENT') || value.includes('PAYOUT') || value.includes('RENT') || value.includes('FEE')) {
      return '/transactions';
    }
    if (value.includes('INSPECTION') || value.includes('SELF_CHECK') || value.includes('SELF-CHECK')) return '/inspections';
    if (value.includes('MAINTENANCE')) return '/maintenance';
    if (value.includes('DOCUMENT')) return '/documents';
    if (value.includes('TENANT')) return '/tenants';
    if (value.includes('LEASE')) return '/leases';
    if (value.includes('REPORT') || value.includes('SUMMARY')) return '/reports';
    return '/dashboard';
  }

  private notificationRoutedUrl(notification: OrganizationNotification): string | null {
    const value = [
      notification.type,
      notification.title,
      notification.message,
      notification.referenceType,
    ].map((part) => part ?? '').join(' ').toUpperCase();

    if (value.includes('INSPECTION_MAINTENANCE_CREATED')) {
      return this.notificationUrl('/maintenance', {
        sourceInspectionId: this.notificationReferenceId(notification, 'INSPECTION'),
      });
    }

    if (value.includes('INSPECTION') || value.includes('SELF_CHECK') || value.includes('SELF-CHECK')) {
      return this.notificationUrl('/inspections', {
        inspectionId: this.notificationReferenceId(notification, 'INSPECTION'),
        propertyId: this.notificationReferenceId(notification, 'PROPERTY'),
      });
    }

    if (value.includes('MAINTENANCE')) {
      return this.notificationUrl('/maintenance', {
        requestId: this.notificationReferenceId(notification, 'MAINTENANCE'),
        propertyId: this.notificationReferenceId(notification, 'PROPERTY'),
        unitId: this.notificationReferenceId(notification, 'UNIT'),
        tenantId: this.notificationReferenceId(notification, 'TENANT'),
      });
    }

    if (value.includes('LATE_FEE_APPLIED') || value.includes('LATE FEE')) {
      return this.notificationUrl('/balances', {
        type: 'late-fee',
        leaseId: String(notification.referenceType ?? '').toUpperCase().includes('LEASE')
          ? notification.referenceId
          : null,
      });
    }

    if (value.includes('OVERDUE_RENT') || value.includes('OVERDUE RENT') || value.includes('OVERDUE CHARGE')) {
      return this.notificationUrl('/balances', { type: 'overdue' });
    }

    return null;
  }

  private notificationReferenceId(notification: OrganizationNotification, referenceType: string): string | null {
    return String(notification.referenceType ?? '').toUpperCase().includes(referenceType)
      ? notification.referenceId ?? null
      : null;
  }

  private notificationUrl(path: string, params: Record<string, string | null | undefined> = {}): string {
    const query = new URLSearchParams();
    const orgId = this.notificationOrgId || this.activeWorkspaceId || this.currentOrgId();
    if (orgId) {
      query.set('orgId', orgId);
    }
    for (const [key, value] of Object.entries(params)) {
      if (value) {
        query.set(key, value);
      }
    }
    const queryString = query.toString();
    return queryString ? `${path}?${queryString}` : path;
  }

  private isPendingInvitationNotification(notification: OrganizationNotification): boolean {
    return notification.id.startsWith('pending-staff-invitation:')
      || notification.id.startsWith('pending-owner-invitation:');
  }

  protected toggleColorScheme(): void {
    this.appearance.toggleColorScheme();
  }

  protected get platformSupportAccessLabel(): string {
    const mode = this.platformSupportSession?.accessMode;
    return mode === 'FULL_ACCESS' ? 'Full access' : 'Read only';
  }

  protected get platformSupportReadOnly(): boolean {
    return this.platformSupportSession?.accessMode === 'READ_ONLY';
  }

  /** The workspace's subscription has ended: everything stays viewable, changes are blocked until it's renewed. */
  protected get subscriptionViewOnly(): boolean {
    if (this.platformSupportSession) {
      return false;
    }
    return this.currentWorkspaceOption()?.subscriptionActive === false;
  }

  /** Either kind of view-only mode; the same click, submit and edit blocking applies to both. */
  protected get viewOnly(): boolean {
    return this.platformSupportReadOnly || this.subscriptionViewOnly;
  }

  protected get canRenewSubscription(): boolean {
    const workspace = this.currentWorkspaceOption();
    return onCapabilityPolicy(workspace) ? hasWorkspacePermission(workspace, 'SUBSCRIPTION_MANAGE') : legacyBroadAccess(workspace);
  }

  protected renewSubscription(): void {
    const orgId = this.currentWorkspaceOption()?.orgId;
    if (orgId && typeof window !== 'undefined') {
      // No returnUrl: the plan page reads orgId + returnUrl as "change an active plan", which the server refuses
      // for the expired or cancelled workspaces that see Renew. Closing the page still returns signed-in users
      // to their workspaces.
      window.location.href = `${environment.publicAppUrl}/subscription/select?orgId=${encodeURIComponent(orgId)}`;
    }
  }

  private currentWorkspaceOption() {
    const orgId = this.activeWorkspaceId || this.notificationOrgId;
    return orgId ? this.workspaceOptions.find((item) => item.orgId === orgId) : undefined;
  }

  protected get platformSupportExpiresLabel(): string {
    const expiresAt = this.platformSupportSession?.expiresAt;
    if (!expiresAt) {
      return 'expires soon';
    }

    const expires = new Date(expiresAt).getTime();
    if (!Number.isFinite(expires)) {
      return 'expires soon';
    }

    const minutes = Math.max(0, Math.ceil((expires - Date.now()) / 60000));
    if (minutes <= 0) {
      return 'expired';
    }
    if (minutes === 1) {
      return 'expires in 1 minute';
    }
    return `expires in ${minutes} minutes`;
  }

  protected async endPlatformSupportSession(): Promise<void> {
    const sessionId = this.platformSupportSession?.id || this.platformSupportSessionId();
    if (!sessionId || this.platformSupportSessionEnding) {
      return;
    }

    this.platformSupportSessionEnding = true;
    this.platformSupportSessionError = null;

    try {
      await firstValueFrom(this.organizations.endSupportSession(sessionId));
      this.clearPlatformSupportSession();
      this.organizations.clearOrganizationContextCache();
      window.location.href = environment.platformAdminAppUrl;
    } catch (error) {
      console.warn('[BackofficeShell] support session end failed:', error);
      this.platformSupportSessionError = 'Unable to end support session right now.';
    } finally {
      this.platformSupportSessionEnding = false;
    }
  }

  private isInsideReadOnlySupportScope(target: Element): boolean {
    return !!target.closest('.backoffice-support-readonly-scope');
  }

  private handleReadOnlySupportClick(event: Event): void {
    if (!this.viewOnly) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.isInsideReadOnlySupportScope(target) || target.closest('.platform-support-banner')) {
      return;
    }

    const interactive = target.closest('button, a, [role="button"], input, select, textarea, [contenteditable="true"]');
    if (interactive && !this.readOnlyInteractionIsAllowed(interactive)) {
      this.blockReadOnlySupportEvent(event);
    }
  }

  private handleReadOnlySupportSubmit(event: Event): void {
    if (!this.viewOnly) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (target && this.isInsideReadOnlySupportScope(target)) {
      this.blockReadOnlySupportEvent(event);
    }
  }

  private handleReadOnlySupportFormChange(event: Event): void {
    if (!this.viewOnly) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.isInsideReadOnlySupportScope(target) || target.closest('.platform-support-banner')) {
      return;
    }

    if (target.matches('input, select, textarea, [contenteditable="true"]')) {
      this.blockReadOnlySupportEvent(event);
    }
  }

  private readOnlyInteractionIsAllowed(interactive: Element): boolean {
    if (interactive.closest('[data-readonly-allowed="true"], .readonly-allowed')) {
      return true;
    }

    if (interactive.matches('input, select, textarea, [contenteditable="true"]')) {
      return false;
    }

    const text = (interactive.textContent || interactive.getAttribute('aria-label') || interactive.getAttribute('title') || '')
      .trim()
      .toLowerCase();
    const icon = interactive.querySelector('.material-symbols-outlined')?.textContent?.trim().toLowerCase() || '';
    const allowedText = [
      'view',
      'open',
      'review',
      'preview',
      'download',
      'export',
      'print',
      'copy',
      'refresh',
      'filter',
      'search',
      'close',
      'cancel',
      'back',
      'next',
      'previous',
      'page',
    ];
    const allowedIcons = [
      'visibility',
      'open_in_new',
      'download',
      'ios_share',
      'content_copy',
      'refresh',
      'filter_list',
      'search',
      'close',
      'arrow_back',
      'arrow_forward',
      'chevron_left',
      'chevron_right',
      'keyboard_arrow_left',
      'keyboard_arrow_right',
    ];

    return allowedText.some((allowed) => text.includes(allowed))
      || allowedIcons.some((allowed) => icon.includes(allowed));
  }

  private blockReadOnlySupportEvent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (this.subscriptionViewOnly) {
      this.subscriptionViewOnlyNotice = this.canRenewSubscription
        ? 'This workspace is view only until its subscription is renewed.'
        : 'This workspace is view only until the owner renews its subscription.';
    } else {
      this.platformSupportSessionError = 'This support session is read-only. Start a full-access session to make changes.';
    }
    this.changeDetector.markForCheck();
  }

  protected get colorSchemeIcon(): string {
    return this.appearance.effectiveColorScheme === 'dark' ? 'light_mode' : 'dark_mode';
  }

  protected get colorSchemeLabel(): string {
    return this.appearance.effectiveColorScheme === 'dark'
      ? this.translations.translate('header.switchToLightMode')
      : this.translations.translate('header.switchToDarkMode');
  }

  protected get greetingKey(): string {
    const hour = new Date().getHours();

    if (hour < 12) {
      return 'header.goodMorning';
    }

    if (hour < 18) {
      return 'header.goodAfternoon';
    }

    return 'header.goodEvening';
  }

  protected get currentDateLabel(): string {
    return this.displayPreferences.formatDate(new Date());
  }

  @HostListener('document:pointerdown', ['$event'])
  protected closeMenusOnOutsideClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    const host = this.elementRef.nativeElement;
    const insideLanguageMenu = host.querySelector('[data-backoffice-language-menu]')?.contains(target);
    const insideNotificationsMenu = host.querySelector('[data-backoffice-notifications-menu]')?.contains(target);
    const insideQuickActionsMenu = host.querySelector('[data-backoffice-quick-actions-menu]')?.contains(target);
    const insideAiCommandMenu = host.querySelector('[data-backoffice-ai-command]')?.contains(target);
    const insideWorkspaceMenu = host.querySelector('[data-backoffice-workspace-menu]')?.contains(target);

    if (this.languageMenuOpen && !insideLanguageMenu) {
      this.languageMenuOpen = false;
    }

    if (this.quickActionsMenuOpen && !insideQuickActionsMenu) {
      this.quickActionsMenuOpen = false;
    }

    if (this.aiCommandMenuOpen && !insideAiCommandMenu) {
      this.closeAiConversation();
    }

    if (this.workspaceMenuOpen && !insideWorkspaceMenu) {
      this.workspaceMenuOpen = false;
    }

    if (this.notificationsMenuOpen && !insideNotificationsMenu) {
      this.notificationsMenuOpen = false;
      this.syncNotificationView();
    }
  }

  @HostListener('document:click', ['$event'])
  protected blockReadOnlySupportClicks(event: MouseEvent): void {
    if (!this.viewOnly) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.isInsideReadOnlySupportScope(target)) {
      return;
    }

    if (target.closest('.platform-support-banner')) {
      return;
    }

    const interactive = target.closest('button, a, [role="button"], input, select, textarea, [contenteditable="true"]');
    if (!interactive || this.readOnlyInteractionIsAllowed(interactive)) {
      return;
    }

    this.blockReadOnlySupportEvent(event);
  }

  @HostListener('document:submit', ['$event'])
  protected blockReadOnlySupportSubmits(event: SubmitEvent): void {
    if (!this.viewOnly) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (target && this.isInsideReadOnlySupportScope(target)) {
      this.blockReadOnlySupportEvent(event);
    }
  }

  @HostListener('document:input', ['$event'])
  @HostListener('document:change', ['$event'])
  protected blockReadOnlySupportFormChanges(event: Event): void {
    if (!this.viewOnly) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.isInsideReadOnlySupportScope(target) || target.closest('.platform-support-banner')) {
      return;
    }

    if (target.matches('input, select, textarea, [contenteditable="true"]')) {
      this.blockReadOnlySupportEvent(event);
    }
  }

  @HostListener('document:keydown.escape')
  protected closeMenus(): void {
    if (this.quickActionsMenuOpen) this.quickActionsTrigger?.nativeElement.focus();
    this.sidebarOpen = false;
    this.languageMenuOpen = false;
    this.notificationsMenuOpen = false;
    this.quickActionsMenuOpen = false;
    this.closeAiConversation();
    this.workspaceMenuOpen = false;
  }

  private closeTransientShellUi(): void {
    this.closeMenus();
    this.aiDraftDrawerOpen = false;
    this.activeAiDraftId = null;
    this.activeAiDraftOrgId = null;
  }

  protected displayName(session: MeResponse | null, profile: UserProfile | null): string {
    const firstName = profile?.firstName?.trim() || session?.firstName?.trim();
    if (firstName) {
      return firstName;
    }

    const displayName = profile?.displayName?.trim() || session?.displayName?.trim();
    if (displayName) {
      return displayName.split(' ')[0] || displayName;
    }

    const email = profile?.email?.trim() || session?.email?.trim();
    if (!email) {
      return '';
    }

    return (
      email
        .split('@')[0]
        .split(/[._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ') || ''
    );
  }

  protected initials(session: MeResponse | null, profile: UserProfile | null): string {
    return this.displayName(session, profile)
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  protected identityLoaded(session: MeResponse | null, profile: UserProfile | null, profileLoaded: boolean): boolean {
    return Boolean(
      profileLoaded ||
        profile?.firstName?.trim() ||
        profile?.displayName?.trim() ||
        profile?.email?.trim() ||
        session?.firstName?.trim() ||
        session?.displayName?.trim() ||
        session?.email?.trim(),
    );
  }

  protected avatarUrl(session: MeResponse | null, profile: UserProfile | null): string | null {
    return (
      profile?.profileImageUrl?.trim() ||
      profile?.profilePictureUrl?.trim() ||
      profile?.avatarUrl?.trim() ||
      profile?.photoUrl?.trim() ||
      session?.profileImageUrl?.trim() ||
      session?.profilePictureUrl?.trim() ||
      session?.avatarUrl?.trim() ||
      session?.photoUrl?.trim() ||
      null
    );
  }

  private readStoredSidebarState(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private storeSidebarState(): void {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(this.sidebarCollapsed));
    } catch {
      // Navigation remains usable when browser storage is unavailable.
    }
  }

  private async refreshNotifications(): Promise<void> {
    if (this.notificationRefreshPromise) {
      return this.notificationRefreshPromise;
    }

    this.notificationsLoading = true;
    this.syncNotificationView();
    this.notificationRefreshPromise = this.loadNotifications()
      .finally(() => {
        this.notificationsLoading = false;
        this.notificationRefreshPromise = null;
        this.syncNotificationView();
      });
    return this.notificationRefreshPromise;
  }

  private startNotificationRefreshPolling(): void {
    if (this.notificationRefreshIntervalId !== null || typeof window === 'undefined') {
      return;
    }
    this.notificationRefreshIntervalId = window.setInterval(() => {
      if (!this.shellDestroyed) {
        void this.refreshNotifications();
      }
    }, 60_000);
  }

  private stopNotificationRefreshPolling(): void {
    if (this.notificationRefreshIntervalId === null || typeof window === 'undefined') {
      return;
    }
    window.clearInterval(this.notificationRefreshIntervalId);
    this.notificationRefreshIntervalId = null;
  }

  private async loadNotifications(): Promise<void> {
    try {
      const orgId = await this.resolveNotificationOrgId();
      const pendingInvitationNotifications = await this.loadPendingInvitationNotifications();
      if (!orgId) {
        this.notifications = pendingInvitationNotifications;
        this.unreadNotificationCount = pendingInvitationNotifications.length;
        this.syncNotificationView();
        return;
      }
      this.openNotificationStream(orgId);

      const [page, count] = await Promise.all([
        firstValueFrom(this.organizations.getNotifications(orgId)),
        firstValueFrom(this.organizations.getUnreadNotificationCount(orgId)),
      ]);
      this.notifications = [
        ...pendingInvitationNotifications,
        ...(page.content ?? []),
      ].sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt));
      this.unreadNotificationCount = (count.unreadCount ?? 0) + pendingInvitationNotifications.length;
      this.syncNotificationView();
    } catch (error) {
      console.warn('[BackofficeShell] notification lookup failed:', error);
      const pendingInvitationNotifications = await this.loadPendingInvitationNotifications().catch(() => []);
      this.notifications = pendingInvitationNotifications;
      this.unreadNotificationCount = pendingInvitationNotifications.length;
      this.syncNotificationView();
    }
  }

  private async loadPendingInvitationNotifications(): Promise<OrganizationNotification[]> {
    const [ownerInvitations, staffInvitations] = await Promise.all([
      firstValueFrom(this.organizations.getMyOwnerInvitations()).catch(() => [] as OwnerInvitation[]),
      firstValueFrom(this.organizations.getMyStaffInvitations()).catch(() => [] as StaffInvitation[]),
    ]);

    return [
      ...(ownerInvitations ?? []).map((invitation) => this.ownerInvitationNotification(invitation)),
      ...(staffInvitations ?? []).map((invitation) => this.staffInvitationNotification(invitation)),
    ];
  }

  private ownerInvitationNotification(invitation: OwnerInvitation): OrganizationNotification {
    return {
      id: `pending-owner-invitation:${invitation.id}`,
      type: 'OWNER_INVITED',
      title: 'Owner portal invitation',
      message: `You were invited to access ${invitation.organizationName || 'a workspace'} as a property owner.`,
      actionUrl: this.pendingInvitationUrl(invitation.id, 'owner'),
      referenceType: 'OWNER_INVITATION',
      referenceId: invitation.id,
      read: false,
      readAt: null,
      createdAt: this.ownerInvitationSentAt(invitation),
    };
  }

  /**
   * The owner-invitation list has no sent date; it is derived from the expiry (owner invitations last
   * OWNER_INVITATION_VALID_DAYS days) so the item does not show as "Just now" and jump to the top.
   */
  private ownerInvitationSentAt(invitation: OwnerInvitation): string {
    if (invitation.createdAt) {
      return this.notificationDate(invitation.createdAt);
    }
    const expires = invitation.expiresAt ? Date.parse(invitation.expiresAt) : NaN;
    return Number.isNaN(expires)
      ? new Date(0).toISOString()
      : new Date(expires - OWNER_INVITATION_VALID_DAYS * 86_400_000).toISOString();
  }

  private staffInvitationNotification(invitation: StaffInvitation): OrganizationNotification {
    const role = this.humanizeRole(invitation.role || 'team member');
    return {
      id: `pending-staff-invitation:${invitation.id}`,
      type: 'STAFF_INVITED',
      title: 'Workspace invitation',
      message: `You were invited to join ${invitation.organizationName || 'a workspace'} as ${role}.`,
      actionUrl: this.pendingInvitationUrl(invitation.id, 'staff'),
      referenceType: 'STAFF_INVITATION',
      referenceId: invitation.id,
      read: false,
      readAt: null,
      createdAt: this.notificationDate(invitation.invitedAt || invitation.expiresAt),
    };
  }

  private pendingInvitationUrl(invitationId: string, invitationType: 'owner' | 'staff'): string {
    if (invitationType === 'staff') {
      return `${environment.publicAppUrl.replace(/\/$/, '')}/team-invitations/${encodeURIComponent(invitationId)}`;
    }

    const params = new URLSearchParams({
      invitationId,
      invitationType,
    });
    return `${environment.publicAppUrl.replace(/\/$/, '')}/select-workspace?${params.toString()}`;
  }

  private notificationDate(value: string | null | undefined): string {
    const parsed = value ? Date.parse(value) : NaN;
    return Number.isNaN(parsed) ? new Date().toISOString() : new Date(parsed).toISOString();
  }

  private humanizeRole(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  protected async loadAiRecentDrafts(loadMore = false): Promise<void> {
    if (this.aiRecentDraftsLoading || (!loadMore && this.aiRecentDraftsLoaded)) {
      this.syncNotificationView();
      return;
    }

    const generation = this.aiWorkspaceGeneration;
    const current = () => generation === this.aiWorkspaceGeneration && !this.shellDestroyed;
    this.aiRecentDraftsLoading = true;
    this.aiRecentDraftsErrorKey = null;
    this.syncNotificationView();
    try {
      const orgId = await this.resolveNotificationOrgId().catch(() => null);
      if (!current()) return;
      if (!orgId) {
        this.aiRecentDrafts = [];
        this.aiRecentDraftsErrorKey = 'header.ai.error.noWorkspace';
        this.aiRecentDraftsLoaded = true;
        return;
      }

      const page = await firstValueFrom(this.ai.listDraftPage(orgId, loadMore ? this.aiDraftCursor : null));
      if (!current() || this.activeWorkspaceId !== orgId) return;
      const drafts = [...(loadMore ? this.aiRecentDrafts : []), ...page.content];
      this.aiRecentDrafts = drafts
        .filter((draft, index) => drafts.findIndex((candidate) => candidate.id === draft.id) === index)
        .filter((draft) => this.isAiDraftResult(draft))
        .filter((draft) => this.isVisibleRecentAiDraftStatus(draft.status))
        .filter((draft) => !this.isAiDraftDismissed(draft));
      this.aiDraftCursor = page.nextCursor;
      this.aiRecentDraftsLoaded = true;
    } catch (error) {
      if (!current()) return;
      console.warn('[BackofficeShell] Apex AI draft lookup failed:', error);
      if (!loadMore) this.aiRecentDrafts = [];
      this.aiRecentDraftsErrorKey = 'header.ai.error.draftsFailed';
    } finally {
      if (!current()) return;
      this.aiRecentDraftsLoading = false;
      this.syncNotificationView();
    }
  }

  private mergeAiDraft(draft: AiDraftResponse, drafts: AiDraftResponse[]): AiDraftResponse[] {
    return [draft, ...drafts.filter((candidate) => candidate.id !== draft.id)]
      .filter((candidate) => this.isVisibleRecentAiDraftStatus(candidate.status))
      .filter((candidate) => this.isAiDraftResult(candidate))
      .filter((candidate) => !this.isAiDraftDismissed(candidate));
  }

  protected needsAiExecutionReview(draft: AiDraftResponse): boolean {
    return !!draft.executionProtocol && (['FAILED', 'EXECUTION_PENDING'].includes(draft.status)
      || (!!draft.execution && draft.execution.state !== 'SUCCEEDED'));
  }

  private isAiDraftDismissed(draft: AiDraftResponse): boolean {
    if (this.needsAiExecutionReview(draft)) return false;
    return this.aiDraftDismissKeys(draft).some((key) => this.dismissedAiDraftIds.has(key));
  }

  private aiDraftDismissKeys(draft: AiDraftResponse): string[] {
    const keys = [draft.id];
    if (draft.auditId) {
      keys.push(`audit:${draft.auditId}`);
    }
    const createdKey = draft.createdAt || draft.updatedAt || '';
    const signature = [
      draft.organizationId,
      draft.intent || 'UNKNOWN',
      draft.prompt || '',
      draft.summary || '',
      createdKey,
    ].join('|').trim();
    if (signature.length > 4) {
      keys.push(`sig:${signature}`);
    }
    return keys;
  }

  private isVisibleRecentAiDraftStatus(status: string | null | undefined): boolean {
    return [
      'PENDING_REVIEW',
      'NEEDS_MORE_INFO',
      'APPROVED',
      'EXECUTION_PENDING',
      'EXECUTED',
      'FAILED',
    ].includes((status || '').toUpperCase());
  }

  private aiDraftIntentTranslationKey(intent: string | null | undefined): string {
    switch ((intent || 'UNKNOWN').toString().toUpperCase()) {
      case 'CREATE_MAINTENANCE_REQUEST':
        return 'maintenance_request';
      case 'CREATE_TENANT_DRAFT':
        return 'tenant_draft';
      case 'CREATE_LEASE_DRAFT':
        return 'lease_draft';
      case 'CREATE_INSPECTION':
      case 'SCHEDULE_INSPECTION':
      case 'CREATE_INSPECTION_DRAFT':
        return 'inspection_draft';
      case 'CREATE_TRANSACTION_DRAFT':
        return 'transaction_draft';
      case 'CREATE_CALENDAR_EVENT':
        return 'calendar_event';
      case 'CREATE_PROPERTY_WITH_UNITS':
        return 'property_setup';
      case 'EXPLAIN_REPORT_SUMMARY':
        return 'report_explanation';
      case 'SEARCH_RECORDS':
        return 'search_records';
      case 'DRAFT_MESSAGE':
        return 'tenant_message';
      default:
        return 'unknown';
    }
  }

  private readDismissedAiDraftIds(): Set<string> {
    if (typeof window === 'undefined') {
      return new Set();
    }

    try {
      const raw = window.localStorage.getItem(DISMISSED_AI_DRAFTS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? new Set(parsed.filter((value) => typeof value === 'string')) : new Set();
    } catch {
      return new Set();
    }
  }

  private storeDismissedAiDraftIds(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(DISMISSED_AI_DRAFTS_STORAGE_KEY, JSON.stringify([...this.dismissedAiDraftIds].slice(-250)));
    } catch {
      // Ignore storage failures; dismiss still works for the current session.
    }
  }

  private createUiId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private syncNotificationView(): void {
    if (this.shellDestroyed) {
      return;
    }

    this.zone.run(() => {
      try {
        this.changeDetector.detectChanges();
      } catch {
        this.changeDetector.markForCheck();
      }
    });
  }

  private scheduleAiConversationScroll(behavior: ScrollBehavior = 'smooth'): void {
    if (this.shellDestroyed || typeof window === 'undefined') {
      return;
    }

    this.zone.runOutsideAngular(() => {
      if (this.aiConversationScrollFrame !== null) {
        window.cancelAnimationFrame(this.aiConversationScrollFrame);
      }

      this.aiConversationScrollFrame = window.requestAnimationFrame(() => {
        this.aiConversationScrollFrame = window.requestAnimationFrame(() => {
          this.aiConversationScrollFrame = null;
          this.scrollAiConversationToBottom(behavior);
        });
      });
    });
  }

  private scrollAiConversationToBottom(behavior: ScrollBehavior): void {
    const container = this.aiConversationScroll?.nativeElement;
    if (!container) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  }

  private async resolveNotificationOrgId(): Promise<string | null> {
    if (this.notificationOrgId && this.workspaceOptions.length > 0) {
      return this.notificationOrgId;
    }

    if (this.workspaceContextPromise) {
      return this.workspaceContextPromise;
    }

    this.workspaceContextPromise = this.loadWorkspaceContext()
      .catch((error) => {
        console.warn('[BackofficeShell] workspace context lookup failed:', error);
        this.workspaceOptions = [];
        this.activeWorkspaceId = null;
        this.notificationOrgId = null;
        this.workspaceName = '';
        this.workspaceLogoUrl = null;
        this.workspaceLoading = false;
        this.syncNotificationView();
        return null;
      })
      .finally(() => {
        this.workspaceContextPromise = null;
      });
    return this.workspaceContextPromise;
  }

  private async loadWorkspaceContext(): Promise<string | null> {
    this.workspaceLoading = true;
    this.accountingNavigationAccess.set(null);
    ++this.accountingNavigationRequest;
    const context = await firstValueFrom(this.organizations.getOrganizationContext());
    this.workspaceOptions = context.organizations ?? [];
    await this.loadOrganizationCreationAccess(this.workspaceOptions.length);
    const hostOrgId = await this.resolveWorkspaceOrgIdFromHost();
    const preferredOrgId = hostOrgId || this.currentOrgId();
    const preferredOrg = preferredOrgId
      ? this.workspaceOptions.find((organization) => organization.orgId === preferredOrgId)
      : null;
    if (hostOrgId && !preferredOrg) {
      this.notificationOrgId = null;
      this.activeWorkspaceId = null;
      this.activeWorkspaceSubscriptionStatus = null;
      this.activeWorkspaceSubscriptionLoaded = true;
      this.activeWorkspacePlan.set(null);
      this.workspaceName = '';
      this.workspaceLogoUrl = null;
      this.workspaceLoading = false;
      this.syncNotificationView();
      return null;
    }
    this.notificationOrgId = preferredOrg?.orgId ?? context.defaultOrgId ?? context.organizations[0]?.orgId ?? null;
    if (this.activeWorkspaceId !== this.notificationOrgId) this.resetAiWorkspace();
    this.activeWorkspaceId = this.notificationOrgId;
    void this.loadAccountingNavigationAccess(this.activeWorkspaceId);
    const activeOrganization = this.workspaceOptions.find((organization) => organization.orgId === this.notificationOrgId);
    this.workspaceName = activeOrganization?.orgName?.trim() || context.defaultOrgName?.trim() || '';
    await this.loadActiveWorkspaceSubscriptionAccess(this.notificationOrgId);
    if (this.notificationOrgId) {
      this.rememberWorkspace(this.notificationOrgId);
    }
    return this.notificationOrgId;
  }

  private async loadAccountingNavigationAccess(orgId: string | null): Promise<void> {
    const request = ++this.accountingNavigationRequest;
    this.accountingNavigationAccess.set(null);
    if (!orgId) return;

    let canRead: boolean | null = null;
    try {
      const access = await firstValueFrom(this.accounting.getCapabilities(orgId).pipe(
        timeout(8000),
        takeUntilDestroyed(this.destroyRef),
      ));
      canRead = access.organizationId === orgId && access.canRead === true;
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        canRead = false;
      }
    }
    if (!this.shellDestroyed && request === this.accountingNavigationRequest && this.activeWorkspaceId === orgId) {
      this.accountingNavigationAccess.set({ orgId, canRead });
    }
  }

  private async loadOrganizationCreationAccess(visibleOrganizationCount: number): Promise<void> {
    const subscriptionStatus = await firstValueFrom(this.subscriptions.getMySubscription()).catch(() => null);
    this.canCreateOrganization = this.canCreateWorkspace(subscriptionStatus, visibleOrganizationCount);
  }

  private async loadActiveWorkspaceSubscriptionAccess(orgId: string | null): Promise<void> {
    this.activeWorkspaceSubscriptionLoaded = false;
    if (!orgId) {
      this.activeWorkspaceSubscriptionStatus = null;
      this.activeWorkspaceSubscriptionLoaded = true;
      return;
    }

    this.activeWorkspaceSubscriptionStatus = await firstValueFrom(this.subscriptions.getMySubscription(orgId)).catch(() => null);
    this.activeWorkspaceSubscriptionLoaded = true;
    this.activeWorkspacePlan.set(this.activeWorkspaceSubscriptionStatus);
  }

  /** Whether the workspace's plan lacks this item's feature. Unknown (loading or failed) isn't treated as lacking it. */
  protected lockedByPlan(item: Pick<NavItem, 'planFeature' | 'route'>): boolean {
    const plan = this.activeWorkspacePlan();
    const feature = item.planFeature ?? managedFeatureForRoute(item.route ?? '');
    return Boolean(feature && plan?.featureEnforcement === true && !planIncludes(plan, feature));
  }

  private canAddPropertyFromSubscription(): boolean {
    if (!this.activeWorkspaceSubscriptionLoaded) {
      return false;
    }

    const subscriptionStatus = this.activeWorkspaceSubscriptionStatus;
    if (!planIncludes(subscriptionStatus, 'PROPERTY_MANAGEMENT')) {
      return false;
    }

    if (!subscriptionStatus?.active) {
      return false;
    }

    if (typeof subscriptionStatus.canAddProperty === 'boolean') {
      return subscriptionStatus.canAddProperty;
    }

    if (subscriptionStatus.unlimitedProperties) {
      return true;
    }

    const maxProperties = Number(subscriptionStatus.maxPropertiesPerOrg);
    if (!Number.isFinite(maxProperties) || maxProperties <= 0) {
      return true;
    }

    return Number(subscriptionStatus.propertiesUsed ?? 0) < maxProperties;
  }

  private canCreateWorkspace(subscriptionStatus: any, visibleOrganizationCount: number): boolean {
    if (!subscriptionStatus?.active) {
      return false;
    }

    if (subscriptionStatus.unlimitedOrganizations) {
      return true;
    }

    const maxOrganizations = Number(subscriptionStatus.maxOrganizations);
    if (!Number.isFinite(maxOrganizations) || maxOrganizations <= 0) {
      return typeof subscriptionStatus.canCreateOrganization === 'boolean'
        ? subscriptionStatus.canCreateOrganization
        : true;
    }

    const organizationsUsed = Math.max(
      Number(subscriptionStatus.organizationsUsed ?? 0),
      visibleOrganizationCount,
    );

    return organizationsUsed < maxOrganizations;
  }

  private publicSetupUrl(path: string): string {
    const url = new URL(path, environment.publicAppUrl);
    if (typeof window !== 'undefined') {
      url.searchParams.set('returnUrl', window.location.href);
    }
    return url.toString();
  }

  private async initializeWorkspaceBranding(resolvedOrgId?: string | null): Promise<void> {
    const orgId = resolvedOrgId || await this.resolveNotificationOrgId().catch(() => null);
    if (!orgId) {
      this.workspaceLogoUrl = null;
      this.workspaceLoading = false;
      this.syncNotificationView();
      return;
    }

    try {
      const branding = await firstValueFrom(this.organizations.getOrganizationBranding(orgId));
      this.activeWorkspaceId = orgId;
      this.workspaceName = branding.orgName?.trim() || this.workspaceName;
      this.workspaceLogoUrl = this.brandingImageUrl(branding.logoUrl?.trim() || null);
      this.applyWorkspaceBrandingColors(branding.primaryColor, branding.accentColor);
      this.rememberWorkspaceBranding(orgId, branding.primaryColor, branding.accentColor);
      this.displayPreferences.setCurrency(resolveCurrency(branding.currency, null, branding.locale));
      this.displayPreferences.setWorkspacePreferences(branding);
      this.workspaceLoading = false;
      this.syncNotificationView();
    } catch {
      this.workspaceLogoUrl = null;
      if (!this.hasCachedWorkspaceBranding()) {
        this.applyWorkspaceBrandingColors(null, null);
      }
      this.workspaceLoading = false;
    }
  }

  private applyWorkspaceBrandingColors(primary?: string | null, accent?: string | null): void {
    if (typeof document === 'undefined') {
      return;
    }

    const primaryColor = this.normalizeBrandColor(primary, '#2FC1F1');
    const accentColor = this.normalizeBrandColor(accent, primaryColor);

    document.documentElement.style.setProperty('--workspace-primary', primaryColor);
    document.documentElement.style.setProperty('--workspace-accent', accentColor);
    document.documentElement.style.setProperty('--workspace-secondary', accentColor);
  }

  private normalizeBrandColor(value: string | null | undefined, fallback: string): string {
    const color = value?.trim();
    return color && /^#[0-9A-Fa-f]{6}$/.test(color) ? color : fallback;
  }

  private rememberWorkspaceBranding(orgId: string, primary?: string | null, accent?: string | null): void {
    if (typeof window === 'undefined') {
      return;
    }

    const primaryColor = this.normalizeBrandColor(primary, '#2FC1F1');
    const accentColor = this.normalizeBrandColor(accent, primaryColor);

    try {
      const raw = window.localStorage.getItem(WORKSPACE_BRANDING_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const byOrg = parsed && typeof parsed === 'object' && parsed.byOrg && typeof parsed.byOrg === 'object'
        ? parsed.byOrg
        : {};
      window.localStorage.setItem(WORKSPACE_BRANDING_STORAGE_KEY, JSON.stringify({
        lastOrgId: orgId,
        byOrg: {
          ...byOrg,
          [orgId]: { primaryColor, accentColor },
        },
      }));
    } catch {
      // Cached branding only prevents refresh flicker; live branding still works without storage.
    }
  }

  private hasCachedWorkspaceBranding(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const raw = window.localStorage.getItem(WORKSPACE_BRANDING_STORAGE_KEY);
      return !!raw && raw.includes('primaryColor');
    } catch {
      return false;
    }
  }

  private brandingImageUrl(url: string | null): string | null {
    if (!url) {
      return null;
    }

    if (url.startsWith('data:') || url.startsWith('blob:')) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${Date.now()}`;
  }

  private withCurrentOrg(queryParams: Record<string, string>): Record<string, string> {
    const orgId = this.activeWorkspaceId || this.currentOrgId();
    return orgId ? { ...queryParams, orgId } : queryParams;
  }

  private async loadPlatformSupportSession(): Promise<void> {
    if (!this.platformSupportSessionId()) {
      this.platformSupportSession = null;
      return;
    }

    this.platformSupportSessionLoading = true;
    this.platformSupportSessionError = null;

    try {
      this.platformSupportSession = await firstValueFrom(this.organizations.getCurrentSupportSession());
    } catch (error) {
      console.warn('[BackofficeShell] support session lookup failed:', error);
      this.platformSupportSession = null;
      this.clearPlatformSupportSession();
    } finally {
      this.platformSupportSessionLoading = false;
    }
  }

  private platformSupportSessionId(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.sessionStorage.getItem(PLATFORM_SUPPORT_SESSION_STORAGE_KEY)?.trim() || null;
    } catch {
      return null;
    }
  }

  private clearPlatformSupportSession(): void {
    this.platformSupportSession = null;
    this.platformSupportSessionError = null;

    try {
      window.sessionStorage.removeItem(PLATFORM_SUPPORT_SESSION_STORAGE_KEY);
    } catch {
      // Storage is best-effort only.
    }
  }

  private currentOrgId(): string | null {
    try {
      const currentUrl = this.router.parseUrl(this.router.url);
      const routeOrgId = currentUrl.queryParams['orgId'];
      if (typeof routeOrgId === 'string' && routeOrgId.trim()) {
        return routeOrgId.trim();
      }

      return window.sessionStorage.getItem('selectedOrgId')?.trim() || null;
    } catch {
      return null;
    }
  }

  private async resolveWorkspaceOrgIdFromHost(): Promise<string | null> {
    const workspace = this.workspaceKeyFromBrowser();
    if (!workspace) {
      return null;
    }

    if (!this.hostWorkspaceOrgIdPromise) {
      this.hostWorkspaceOrgIdPromise = firstValueFrom(this.organizations.getPublicWorkspaceBranding(workspace))
        .then((branding) => branding.orgId?.trim() || null)
        .catch((error) => {
          console.warn('[BackofficeShell] workspace host lookup failed:', error);
          return null;
        })
        .finally(() => {
          this.hostWorkspaceOrgIdPromise = null;
        });
    }

    return this.hostWorkspaceOrgIdPromise;
  }

  private workspaceKeyFromBrowser(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const url = new URL(window.location.href);
    const queryWorkspace = url.searchParams.get('workspace') || url.searchParams.get('org');
    if (queryWorkspace?.trim()) {
      return queryWorkspace.trim();
    }

    const host = url.hostname.toLowerCase();
    if (!host || host.includes('localhost')) {
      return null;
    }

    const portalHosts = [
      'backoffice-dev.apex-habitat.com',
      'backoffice.apex-habitat.com',
      'tenant-dev.apex-habitat.com',
      'tenant.apex-habitat.com',
      'owner-dev.apex-habitat.com',
      'owner.apex-habitat.com',
      'platform-admin-dev.apex-habitat.com',
      'platform-admin.apex-habitat.com',
      'admin-dev.apex-habitat.com',
      'admin.apex-habitat.com',
    ];

    for (const portalHost of portalHosts) {
      if (host === portalHost) {
        return null;
      }

      if (host.endsWith(`.${portalHost}`)) {
        return host.slice(0, -(portalHost.length + 1)).split('.')[0] || null;
      }
    }

    return host;
  }

  private rememberWorkspace(orgId: string): void {
    try {
      window.sessionStorage.setItem('selectedOrgId', orgId);
    } catch {
      // Storage is best-effort only.
    }
  }

  private workspaceDashboardUrl(organization: OrganizationContextItem): string {
    const url = new URL(this.workspaceAppBaseUrl(organization));
    const portalSlug = organization.portalSlug?.trim().toLowerCase();

    if (
      portalSlug &&
      url.hostname !== 'localhost' &&
      !/^\d{1,3}(\.\d{1,3}){3}$/.test(url.hostname) &&
      !url.hostname.startsWith(`${portalSlug}.`)
    ) {
      url.hostname = `${portalSlug}.${url.hostname}`;
    }

    url.pathname = '/dashboard';
    url.searchParams.set('orgId', organization.orgId);
    return url.toString();
  }

  private workspaceAppBaseUrl(organization: OrganizationContextItem): string {
    const portalMode = (organization.portalMode || '').toUpperCase();
    if (portalMode === 'TENANT') {
      return environment.tenantAppUrl;
    }
    if (portalMode === 'OWNER_PORTAL') {
      return environment.ownerAppUrl;
    }
    return environment.backofficeAppUrl;
  }

  private async initializeNotificationStream(): Promise<void> {
    const orgId = await this.resolveNotificationOrgId().catch(() => null);
    if (orgId) {
      this.openNotificationStream(orgId);
    }
  }

  private openNotificationStream(orgId: string): void {
    if (typeof window === 'undefined' || this.notificationEventSource) {
      return;
    }

    this.notificationEventSource = new EventSource(
      this.organizations.getNotificationStreamUrl(orgId),
      { withCredentials: true },
    );

    this.notificationEventSource.addEventListener('connected', (event) => {
      this.applyUnreadCountEvent(event);
    });

    this.notificationEventSource.addEventListener('unread-count', (event) => {
      this.applyUnreadCountEvent(event);
    });

    this.notificationEventSource.addEventListener('notification', (event) => {
      const notification = this.parseEventData<OrganizationNotification>(event);
      if (!notification?.id) {
        return;
      }
      const previous = this.notifications.find((item) => item.id === notification.id) ?? null;
      this.notifications = [
        notification,
        ...this.notifications.filter((item) => item.id !== notification.id),
      ].slice(0, 10);
      this.unreadNotificationCount = nextUnreadCount(this.unreadNotificationCount, previous, notification);
      this.syncNotificationView();
    });
  }

  private closeNotificationStream(): void {
    this.notificationEventSource?.close();
    this.notificationEventSource = null;
  }

  private applyUnreadCountEvent(event: Event): void {
    const data = this.parseEventData<{ unreadCount?: number }>(event);
    this.unreadNotificationCount = data?.unreadCount ?? this.unreadNotificationCount;
    this.syncNotificationView();
  }

  private parseEventData<T>(event: Event): T | null {
    if (!('data' in event) || typeof event.data !== 'string') {
      return null;
    }
    try {
      return JSON.parse(event.data) as T;
    } catch {
      return null;
    }
  }

  protected async logout(): Promise<void> {
    try {
      await this.auth.logout();
    } finally {
      this.profile.clearProfile();
      window.location.href = `${environment.publicAppUrl}/login`;
    }
  }
}
